/* /homepage — fixed Seaglass Green presentation.
   The homepage no longer re-themes: it always renders sea-glass (the strongest
   look), and colour choice moved to the wall of tiles, each of which deep-links
   the PDP with that Shopify variant preselected. The
   theme engine stays because the PDP contract and the h-key review sets use it. */
import { THEMES } from './tao-mockup-themes.js?v=19colors2';

const root = document.documentElement;
const $  = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => r ? [...r.querySelectorAll(s)] : [];
const byId = new Map(THEMES.map(t => [t.id, t]));
/* Save-Data, or a phone with 2GB or less. Nothing below stops working under it; the page
   just stops spending ahead of need — no idle warming of the whole colourway set, no
   priming of the hero clips, hero preload held at metadata. */
const LEAN = navigator.connection?.saveData || (navigator.deviceMemory || 8) <= 2;
let productUrl = document.querySelector('[data-tao-mockup-page="home"]')?.dataset.productUrl || '#';
let variantsByTheme = new Map();
let roomImagesByTheme = {};
let roomImageSizesByTheme = {};
let hiddenThemeIds = new Set();
try {
  const productData = JSON.parse(document.querySelector('[data-tao-mockup-home-product]')?.textContent || '{}');
  productUrl = productData.url || productUrl;
  variantsByTheme = new Map((productData.variants || []).map(variant => [variant.themeId, variant]));
  roomImagesByTheme = productData.roomImages || {};
  roomImageSizesByTheme = productData.roomImageSizes || {};
  /* Sold-out colourways Liquid leaves off the wall ("Hide sold-out variants (US)" setting). */
  hiddenThemeIds = new Set(productData.hiddenThemeIds || []);
} catch (error) {
  console.warn('TAO homepage variant data could not be parsed.', error);
}

/* Room captions for the color wall — order = THEMES order. */
const ROOM = {
  'sea-glass':'garden light.', 'marbel':'quiet palette.', 'glacier-blue':'winter sun.',
  'champagne':'morning travertine.', 'midnight-blue':'after dark.', 'pink':'powder room.',
  'london-fog':'soft concrete.', 'lilac':'violet hour.', 'eclipse':'candlelight.',
  'rose-gold':'warm brass.', 'matte-black':'midnight balance.', 'metallic-teal':'coastal depth.',
  'evergreen':'garden shadow.',
};

/* No hero or morning slot: both are <video> now, so there is no per-colourway still to
   resolve for either. setImg guards on tagName as well, because a slot quietly turning
   into a video is a recurring move in this file and assigning a .webp to video.src
   fails silently — it does not throw, it just blanks the element. */
const els = {
  rooms:   $('#hp-rooms-grid'),
  uv:      $('#hp-uv-img'),
  quote:   $('.hp-quote-bg'),
  meta:    $('meta[name="theme-color"]'),
};

/* image resolution with graceful fallback while renders land.

   The markup ships each of these slots already pointing at the sea-glass asset it
   will resolve to, so the common case must be a NO-OP: reassigning a src the browser
   is already loading throws away the in-flight request and, when the two differ, is
   visible as a flash of the wrong image on load. So compare paths and leave a
   matching element alone — a differing ?v= is not a reason to re-fetch, since the
   markup's own query string is what busts these four. Only a genuinely different
   file (a review-set override, or another colourway) triggers an assignment. */
function setImg(img, srcs) {
  if (!img || img.tagName !== 'IMG') return;
  const [first, ...rest] = srcs;
  const path = u => (u || '').split('?')[0];
  if (path(img.getAttribute('src')) === path(first)) return;
  img.onerror = () => {
    /* A sized variant that fails drops back to the full asset first — the srcset has to
       come OFF, or the browser keeps choosing from it — and only then to the next file. */
    if (img.srcset) { img.removeAttribute('srcset'); img.removeAttribute('sizes'); return; }
    if (rest.length) setImg(img, rest); else img.onerror = null;
  };
  img.src = first;
}
/* AV bumps whenever the gated renders are replaced — the filenames are stable,
   so without it the browser keeps serving the previous round's images.
   It must be spelled `v=...`: the Files CDN strips unrecognised query tokens
   from its cache key (?v27 and ?v28xyz serve one cached object), so a bare
   token busts only browsers while every edge keeps handing out the old bytes.
   `v=` is the one parameter the CDN honours end to end. */
const AV = 'v=202608282';
const tileSrcs = t => [
  roomImagesByTheme[t.id],
  `https://cdn.shopify.com/s/files/1/1009/8326/files/tao-mockup--home3--tile-${t.id}.webp?${AV}`,
  `https://cdn.shopify.com/s/files/1/1009/8326/files/tao-mockup--front--${t.id}.webp`,
].filter(Boolean);
/* the UV macro is shot per colourway too, so the dock in the proof band is the one
   the visitor just picked; uv-cone is the single-colour fallback */
const uvSrcs      = t => [`https://cdn.shopify.com/s/files/1/1009/8326/files/tao-mockup--home3--uv-${t.id}.webp?${AV}`, `https://cdn.shopify.com/s/files/1/1009/8326/files/tao-mockup--home3--uv-cone.webp?v4`];
const quoteSrcs   = t => [`https://cdn.shopify.com/s/files/1/1009/8326/files/tao-mockup--home3--quote-${t.id}.webp?${AV}`, `https://cdn.shopify.com/s/files/1/1009/8326/files/tao-mockup--home3--quote-wide.webp?v3`];

export function applyTheme(id, { initial = false, echo = false } = {}) {
  const t = byId.get(id) || THEMES[3];
  const set = (k, v) => root.style.setProperty(k, v);
  set('--bg', t.bg2); set('--bg-2', t.bg2);
  set('--ink', t.ink); set('--muted', t.muted);
  set('--accent', t.accent); set('--accent-ink', t.accentInk); set('--accent-strong', t.accentStrong);
  set('--surface', t.surface); set('--surface-strong', t.surfaceStrong);
  set('--hairline', t.hairline);
  document.body.dataset.mode = t.mode;
  root.dataset.bootTheme = t.id;
  if (els.meta) els.meta.content = t.bg2;

  setImg(els.uv, uvSrcs(t));
  setImg(els.quote, quoteSrcs(t));

  /* deliberately no localStorage write and no URL rewrite: the homepage is pinned
     to sea-glass and must not stomp the PDP's saved colour */
}

function build() {
  /* the colour wall — every tile is a real link to the exact Shopify variant.
     Liquid supplies IDs from the live catalog, so this never relies on visual
     color handles or a duplicated hardcoded ID table. */
  /* The set is SPLIT across the two rows, and disjointly: the row below takes the first
     seven colourways, the row above the remaining six. That is the whole of the
     no-duplicates guarantee at the cross-row level — a colour that is not in a row
     cannot appear in it, however the two drift against each other. Within a row it falls
     out of the tile size; see css/home3.css.

     The heading is markup now rather than built here. It used to be the grid's first
     cell, hoisted out of the row by JS on a phone and left in it on desktop, because
     desktop was a wall and the heading was one of its cells. With two rows it belongs
     between them, which is a place in the document — so it lives in the document. */
  /* Half the set, computed rather than written down: adding colourways should widen both
     rows, not lengthen one of them. At thirteen it is 7 and 6. */
  /* ── the tile pool ────────────────────────────────────────────────────────
     Two copies of every colourway are MADE here and none are placed. Where they go is a
     question about the viewport, and the viewport can change under a page that has
     already been built — a laptop window dragged narrow, a phone turned on its side, and
     both cross this breakpoint. So placement is layoutRooms()'s job and this is only the
     pool it draws from. */
  /* The wall deals in its own order, not the catalog's. The catalog appends
     colourways as they ship, which parked the three newest — all photographed
     on white — shoulder to shoulder. This sequence alternates scene tones
     instead, and the WRAP counts: each row repeats its slice as a ring, so a
     slice's last tile sits beside its own first again. A colourway missing
     from the list still gets dealt, at the end — new ships must not vanish. */
  const WALL = [
    'marbel', 'midnight-blue', 'pink', 'evergreen', 'cappuccino-taupe',
    'glacier-blue', 'champagne', 'lilac', 'rose-gold', 'desert-sage',
    'smoky-quartz', 'eclipse', 'champagne-pearl', 'metallic-teal',
    'watercolor-sky', 'london-fog', 'sea-glass', 'matte-black', 'marine-layer',
  ];
  const dealt = WALL.map(id => THEMES.find(t => t.id === id)).filter(Boolean)
    .concat(THEMES.filter(t => !WALL.includes(t.id)))
    .filter(t => !hiddenThemeIds.has(t.id));
  /* ── the tile's rendered size, so the browser can pick a cut ────────────────
     The sources are 1600x1600 and the tile never is: 74vw on a phone, and on a desktop
     100vw over the row's period with a 150px floor — the same sums the stylesheet makes
     (see --tile in home.css at both breakpoints). Sixteen tiles decoded at 1600 is ~41MP
     of raster for a row that shows a handful at ~180px; with the CDN's 400 and 900 cuts
     in a srcset the phone decodes 900s and the desktop 400s, and the 1600 stays the
     fallback. The period is the smaller half of the set, as setN() will compute it. */
  const SIZES = '(max-width: 700px) 74vw, max('
    + (100 / Math.max(1, Math.floor(dealt.length / 2))).toFixed(2) + 'vw, 150px)';
  ROOM_POOL.length = 0;
  for (let pass = 0; pass < 2; pass++) {
    ROOM_POOL.push(dealt.map(t => {
      const a = document.createElement('a');
      a.className = 'hp-room'; a.dataset.id = t.id;
      /* The tile wears its own colourway before the photograph lands. A tile with no
         image yet used to be a flat dark rectangle, and on a fast scroll — or any
         connection slower than localhost — a row of those is the "blank spaces" people
         see. Painting the swatch underneath means the worst case is the colour the
         section exists to show, arriving a beat before its photograph, rather than a
         hole. Costs nothing: it is one custom property and no extra request. */
      a.style.setProperty('--tint', t.swatch);
      const variant = variantsByTheme.get(t.id);
      a.href = variant ? `${productUrl}?variant=${variant.id}` : productUrl;
      /* Not draggable. A link and an image are both draggable by default, and a press
         that moves is how you push this row — so without this, reaching for the row
         lifts a translucent copy of the photograph, or the link's URL, and carries it
         with the cursor instead. */
      a.draggable = false;
      /* Only the first copy is real to assistive tech and to the keyboard. The second is
         runway — the same colour, offering the same link — and tabbing through thirteen
         swatches twice is the sort of thing a marquee should never cost anyone. */
      if (pass) { a.setAttribute('aria-hidden', 'true'); a.tabIndex = -1; }
      else a.setAttribute('aria-label', `${t.name} — shop this colour`);
      const img = document.createElement('img');
      img.alt = ''; img.loading = 'lazy'; img.decoding = 'async';
      img.width = 1600; img.height = 1600;
      img.draggable = false;
      /* sizes and srcset BEFORE src, so the first selection is the sized one rather
         than a 1600 fetch that a later srcset write then supersedes */
      const full = roomImagesByTheme[t.id], cuts = roomImageSizesByTheme[t.id];
      if (full && cuts && cuts['400'] && cuts['900']) {
        img.sizes = SIZES;
        img.srcset = `${cuts['400']} 400w, ${cuts['900']} 900w, ${full} 1600w`;
      }
      setImg(img, tileSrcs(t));
      a.append(img);
      a.addEventListener('click', () => { try { localStorage.setItem('tao-theme', t.id); } catch {} });
      return a;
    }));
  }
  layoutRooms();
}

/* ── where the colourway tiles go, which is a question about the viewport ───
   A PHONE GETS ONE ROW. That layout is finished: one tile at a time at 74vw with its
   neighbours breaking the edges, the heading pinned above it. All thirteen colourways
   live in that row, once each, and nothing below is relevant to it.

   A DESKTOP GETS TWO, drifting against each other, and then "never the same colour twice
   on screen" becomes a real constraint with two halves to it.

   The cross-row half is disjointness: the rows carry DISJOINT slices of the set — seven
   below, six above — so a colour that is not in a row cannot appear in it. That is the
   only answer that survives two rows whose relative phase never stops changing; any
   fixed offset between rows that share a colour is a duplicate waiting for the phase to
   come round.

   The in-row half is the repeat. Each row lays its own slice down TWICE, because a ring
   needs runway — tiles beyond the last visible one, on both sides, to re-seat into — and
   six tiles against six visible slots has none; the row would drift off its own end and
   show the ground. Two copies of a colour sit one PERIOD apart (six tiles in the shorter
   row) and the viewport spans at most 1 / 0.18 = 5.56 tiles at any width, so the second
   copy cannot enter before the first has left. See css/home3.css for where the 0.18
   comes from — that number and this one are one argument and must move together. */
const ROOM_POOL = [];
function layoutRooms() {
  const lower = document.getElementById('hp-rooms-grid');
  const upper = document.getElementById('hp-rooms-grid-up');
  if (!lower || !ROOM_POOL.length) return;
  const [p0, p1] = ROOM_POOL;
  /* One row on a phone, in either orientation. Portrait shows one because that is the
     layout; landscape shows one because two rows of square tiles is most of a 393px
     screen. The stylesheet hides the upper row under the same condition — these two have
     to agree, or the colourways dealt into the hidden row simply vanish. */
  const oneRow = matchMedia('(max-width:700px)').matches
    || matchMedia('(orientation: landscape) and (max-height: 520px)').matches;
  if (oneRow) {
    lower.replaceChildren(...p0);
    upper?.replaceChildren();
    setN(lower, p0.length);
  } else {
    const cut = Math.ceil(p0.length / 2);         /* 7 and 6 at thirteen */
    lower.replaceChildren(...p0.slice(0, cut), ...p1.slice(0, cut));
    upper?.replaceChildren(...p0.slice(cut), ...p1.slice(cut));
    setN(lower, cut);
    if (upper) setN(upper, p0.length - cut);
  }
}
/* --n is the row's PERIOD: how many tiles apart two copies of a colour sit. The
   stylesheet sizes the tile from it, so adding colourways widens the rows and shrinks
   the tiles on its own — the guarantee is a formula rather than a number someone has to
   remember to re-derive. Both rows take the SMALLER of the two counts, because the
   constraint is only as strong as the shortest period on screen and the two rows have
   to keep the same pitch to look like one section. */
function setN(grid, n) {
  const other = grid.id === 'hp-rooms-grid-up'
    ? document.getElementById('hp-rooms-grid') : document.getElementById('hp-rooms-grid-up');
  const mine = n;
  const theirs = other && other.childElementCount
    ? new Set([...other.children].map(c => c.dataset.id)).size : mine;
  grid.style.setProperty('--n', String(Math.min(mine, theirs)));
}

build();
applyTheme('sea-glass', { initial: true });

/* No colour stepping, swipe or arrow keys here any more — the homepage colour is
   fixed; picking a colour is the wall's job and it hands off to the PDP. */

/* Deliberately no 'storage' listener. Mirroring the colour live between open tabs
   sounds harmless but it let a second tab drive this one, and with two tabs each
   answering the other's write the colour walked the whole list on load. The carry that
   actually matters — pick a colour here, open the PDP, land in that colour — happens by
   reading the key at startup, which needs no listener. */

/* The nav's glass, its scroll progress bar and the hamburger sheet are all
   tao-mockup-header.js's: the header section loads it on every page, and it drives the
   same markup with the same behaviour. This file used to carry a second copy of each,
   so every scroll ran two rAFs and every menu tap two controllers. */

/* The UV band is a still cutout now, not a video — nothing to play/pause here. */

/* ── hero carousel ──────────────────────────────────────────────────
   The track is a scroll-snap container, so swipe and trackpad scrolling are
   native; the dots and arrows only call scrollTo, and the dots follow the
   scroll position rather than tracking an index we'd have to keep in sync. */
(function heroCarousel() {
  const track = document.getElementById('hp-hero-track');
  const hero  = document.getElementById('hp-hero');
  if (!track || !hero) return;
  const slides = [...track.children];
  const dots   = [...hero.querySelectorAll('.hp-hero-dot')];
  const arrows = [...hero.querySelectorAll('.hp-hero-arrow')];
  if (slides.length < 2) { dots.forEach(d => d.remove()); arrows.forEach(a => a.remove()); return; }

  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  /* LEAN holds both clips at metadata: slide 1 fetches when it plays, which is at once,
     and slide 2 fetches when its slide plays rather than in the first connection. */
  if (LEAN) slides.forEach(sl => sl.querySelectorAll('video').forEach(v => { v.preload = 'metadata'; }));

  /* ── the loop, on mobile ───────────────────────────────────────────────
     A scroll-snap track cannot be endless: past the last slide there is simply no
     content to fling into, and a wrap has to teleport backwards past every slide in
     between — which is why the timer's 3 -> 1 was the one advance that did not move.

     So on mobile the three slides stop being a fixed row and become a window onto a
     ring. The slide showing is always parked in the MIDDLE position with its two
     neighbours either side, so there is always something to swipe to in both
     directions, and every advance — the wrap included — is one width of forward
     travel. After each move the ring rotates back to centre in one synchronous write,
     which is invisible: the slide under the viewport does not change, only the two
     off-screen ones do.

     Rotation is CSS `order`, not DOM moves. Reordering flex items leaves every node
     exactly where it is, which matters because two of these slides are <video>: a
     media element that leaves the document, even for an instant, is a spec-level
     invitation to stop playback.

     Desktop keeps the original behaviour untouched — it has arrows and a visible
     row, and its wrap was a deliberate cut. */
  const N = slides.length;
  const LOOP = matchMedia('(max-width:700px)');
  const looping = () => LOOP.matches && N === 3;
  /* which logical slide sits at each visual position; identity until the ring turns */
  let atPos = slides.map((_, k) => k);
  const W = () => track.clientWidth || 1;   /* a zero here turns every index into NaN */

  /* ── mobile transport: a transform, not a scroll offset ────────────────────
     The ring gave the hero exactly ONE slide of runway either side, and the re-seat
     that restores it can only run while the row is still. A second swipe arriving
     before that — measured at 3 of 6 blocked at a 120ms cadence and 5 of 8 at 60ms —
     finds the track at scrollLeft 750 of a 750 maximum with nothing to scroll into.
     No motion, no scroll event, no advance: the swipe is simply eaten. On a phone it
     is worse than the harness says, because a real fling keeps emitting scroll events
     for 250-400ms after the finger leaves, so the true re-seat latency is the snap
     animation plus 150ms and a deliberate second swipe lands inside it.

     The hero cannot buy runway the way the reel did — cloning a slide would duplicate
     #hp-hero-clean, #hp-hero-sonic, the single .hw-spark heroReveal takes and the
     single .hc-last. So remove the resource instead of rationing it. `overflow:clip`
     is not a scroll container: there is no scrollLeft, no maximum, and no end to
     reach. The track is moved by a transform, and the identity

         (pos, ring)  ==  (pos +/- 1, ring rotated one step the other way)

     is EXACT, because `order` is a pure permutation of three flex items that are each
     100% of the track. So the transport can re-base at any instant, with no stillness
     precondition and nothing to run out of. That is a structural guarantee rather than
     a budget: no input rate exhausts it, because a gesture consumes nothing.

     Layout is byte-identical to the scrolling version — a transform moves paint, not
     layout — so heroReveal's eight published px properties and its ResizeObserver, the
     --floor chain, .hp-hero-in::before's overhang and the ten :nth-child blocks cannot
     observe this and need no re-derivation. */
  let pos = -1;                 /* track offset in viewport widths; -1 is seated */
  let anim = null;
  /* A second swipe arriving mid-settle is QUEUED, not merged into the running move.
     Both alternatives are worse and both were measured: restarting from the seat pops
     the track up to a full width backwards at the instant a finger lands, and
     continuing past the target needs a FOURTH visual position — three slides cannot
     cover a viewport once the offset drifts outside [-2, 0], and it does, so the edge
     of the screen goes blank. Queueing keeps every animation exactly one width from
     the seat, which is the invariant that makes the whole transport provable: there is
     no mid-flight position to recover, so nothing can recover it wrongly. The running
     move is sped up instead, so a hurried visitor gets hurried motion rather than a
     dropped gesture. */
  const QMAX = 3;
  let queue = [];
  /* `cur` is the AUTHORITY for which slide is showing on mobile, not the DOM. Deriving
     it from the ring was a real defect and the gesture harness caught it: the boot seat
     is guarded by looping(), matchMedia can answer false while the viewport is still
     settling, and when it did the ring stayed identity while nothing was translated —
     so atPos[1] said "slide 1" over a painted slide 0, and the visitor's first swipe
     went to slide 2. A variable cannot disagree with itself. */
  let cur = 0, seated = false;
  const DUR = 340;

  /* The DESKTOP transport's state, and it is declared here because it was once not
     declared anywhere at all. Above 700px the hero still moves by tweening scrollLeft —
     `looping()` is false and pageTo is never reached — and `goto()` opens with
     cancelAnimationFrame(tween). The carousel refactor that introduced the transform
     pager replaced the block these two lived in and did not carry them across, so on
     every desktop arrow click, dot tap and dwell expiry that line threw
     ReferenceError: Can't find variable: tween. Modules are strict, so the assignment
     further down could not quietly create a global either.

     It was invisible for fourteen commits because every harness in .hp/ drives the
     phone: the pager is the thing that kept breaking, so the pager is the thing that
     got tested, and the path the refactor did NOT touch is the one that broke.
     .hp/deskhero.html now runs arrows, the dwell bar and the auto-advance at 1200px. */
  let tween = 0, snapTimer = 0;

  /* One step of travel, in pixels, measured off a slide rather than assumed.

     This used to translate by `p * 100%`, and a percentage translate resolves against
     the element's own BORDER box while a `flex:0 0 100%` slide resolves against the
     track's CONTENT box. Those are the same number right up until something reserves a
     gutter — a legacy scrollbar, a zoom quantisation, a subpixel rounding on a 3x
     screen — and then every step overshoots or undershoots the slides it is supposed to
     be stepping through, which is one of the ways the right edge of the viewport can end
     up showing nothing. Translating by the width of the thing being moved cannot
     disagree with the thing being moved. */
  const STEP = () => slides[0].getBoundingClientRect().width || track.clientWidth || 1;

  /* the ONLY writer of track.style.transform */
  function paintPos(p) {
    if (!Number.isFinite(p)) return;
    pos = p;
    track.style.transform = 'translate3d(' + (p * STEP()).toFixed(2) + 'px,0,0)';
  }

  /* the slide actually on screen. Under the ring that is always the middle seat —
     reading it back off a scroll offset was only ever a way of asking the DOM what we
     already knew, and on iOS it asks during rubber-band and gets a lie. */
  let wrapping = false;
  const index = () => (looping() || wrapping) ? cur : Math.round(track.scrollLeft / W());

  /* Idempotent, and called from every entry point rather than once at boot: the first
     paint, a rAF after it, `load`, the breakpoint change, the start of every gesture
     and the start of every programmatic move. Whichever of those first finds a real
     viewport does the seating, and the rest are no-ops. */
  function ensureSeated() {
    if (!looping() || seated) return;
    seatRing(cur);
    paintPos(-1);
    seated = true;
  }

  function setRing(order) {
    atPos = order;
    order.forEach((k, p) => { slides[k].style.order = String(p); });
  }
  /* seat logical slide k in the middle. No scroll write and no snap juggling: the
     caller paints the position, because seating and painting must land in ONE
     synchronous block or the row jumps a whole width for a frame. */
  function seatRing(k) { setRing([(k - 1 + N) % N, k, (k + 1) % N]); }

  /* the single retraction. Order, transform and will-change come off together — half
     a retraction leaves the desktop wall translated or permuted. */
  function unring() {
    if (anim) { anim.cancel(); anim = null; }
    slides.forEach(sl => { sl.style.order = ''; });
    track.style.transform = '';
    track.style.willChange = '';
    atPos = slides.map((_, k) => k);
    pos = -1;
    seated = false;
  }

  /* A named easing, not a sampled one: with queueing there is never a need to ask an
     animation where it has got to, so nothing has to be invertible. */
  function animateTo(to, done) {
    if (anim) { anim.cancel(); anim = null; }
    /* rAF and WAAPI both stall in a hidden tab, and reduced motion asks for no travel
       at all — jump, exactly as the scrolling transport did. */
    if (reduce.matches || document.hidden) { paintPos(to); done(); return; }
    /* one measurement for both keyframes: a resize landing between them would otherwise
       build a move that starts at one scale and ends at another */
    const s = STEP();
    const a = track.animate(
      [{ transform: 'translate3d(' + (pos * s).toFixed(2) + 'px,0,0)' },
       { transform: 'translate3d(' + (to * s).toFixed(2) + 'px,0,0)' }],
      { duration: DUR, easing: 'cubic-bezier(.4,0,.2,1)', fill: 'forwards' });
    anim = a;
    let landed = false;
    const finish = () => {
      if (landed || a !== anim) return;          /* superseded, or already landed */
      landed = true;
      anim = null;
      /* forwards, not none: the commit below runs on the main thread, and anything
         that blocks it past the animation's end — a popup tearing down, a form
         submit — used to let the expired animation revert the track to the slide it
         left, stranding the hero mid-strip until the thread freed. The fill holds
         the landing frame through that gap; committing the position and THEN
         cancelling drops the fill with the underlying style already in place. */
      paintPos(to);
      a.cancel();
      done();
    };
    a.onfinish = finish;
    /* the authority, not onfinish alone: a backgrounded tab can hold a finished
       animation with its event still queued, which would strand the hero mid-travel */
    setTimeout(finish, DUR + 60);
  }

  /* One page of travel, always exactly one width, always starting from the seat. */
  function pageTo(i, dirHint) {
    ensureSeated();
    if (anim) {
      /* already moving: take the gesture as a queued step and hurry the current move
         along rather than dropping either of them */
      if (queue.length < QMAX) queue.push({ i, dirHint });
      /* the deeper the queue, the harder the current move is hurried, so a burst
         resolves quickly instead of trailing a long tail of settles */
      anim.playbackRate = Math.min(4, 1 + queue.length);
      return;
    }
    const from = cur;
    const k = (((i === null ? from + (dirHint || 1) : i) % N) + N) % N;
    if (k === from) { armBar(from); return; }
    const dir = dirHint || ((((k - from + N) % N) === 1) ? 1 : -1);
    const other = [0, 1, 2].find(x => x !== from && x !== k);
    /* Pre-roll. The slide on screen does not move in either direction — forward the ring
       rotates one step and the offset goes to 0, backward both are already where they
       need to be — and both moves then travel exactly one width and LAND on the seat.

       Rotating the ring rather than driving the offset outward is the whole point. Three
       slides span exactly three widths, so an offset of -2 puts the last of them flush
       with the right edge of the viewport with nothing behind it. That is invisible only
       while a slide is exactly as wide as the viewport, and on a real iPhone it is not:
       a photographed transition had the slides 25px narrow, and for the last frames of
       every forward move the page's own sea-glass showed through the gap as a hard band
       down the right edge — the "flash". Keeping the offset inside [-1, 0] means the
       three slides always span [-W, 2W] about the viewport, so there is a full slide of
       cover past both edges at every instant of every move and no width disagreement,
       from a scrollbar, a zoom or subpixel rounding, can open a seam at all. */
    setRing(dir > 0 ? [from, k, other] : [k, from, other]);
    paintPos(dir > 0 ? 0 : -1);
    cur = k;                                     /* the move is committed from here */
    paint();                                     /* media and dwell follow it at once */
    animateTo(dir > 0 ? -1 : 0, () => {
      seatRing(k);
      paintPos(-1);
      track.style.willChange = '';
      paint();
      const next = queue.shift();
      if (next) pageTo(next.i === null ? cur + next.dirHint : next.i, next.dirHint);
    });
  }

  /* One width of travel across the seam of the strip, in the direction the visitor
     asked for. `from` is parked at visual position 1 and the destination at 1 ± 1, so
     the tween is always exactly one width and always the right way round; afterwards
     `order` comes off and scrollLeft goes to the destination's real offset, which is the
     same picture in the same place. `wrapping` holds index() to `cur` while the strip is
     permuted, because scrollLeft / width reports a VISUAL position and would otherwise
     tell the dots and the dwell clock the wrong slide. */
  function wrapRing(n, dir) {
    const W = track.clientWidth;
    const fromIdx = Math.round(track.scrollLeft / W);
    if (fromIdx === n) { armBar(n); return; }
    const ord = [];
    ord[1] = fromIdx;
    ord[1 + dir] = n;
    ord[dir > 0 ? 0 : 2] = slides.map((_, k) => k).find(k => k !== fromIdx && k !== n);
    if (ord.some(k => k === undefined)) { track.scrollLeft = n * W; paint(); return; }

    wrapping = true;
    ord.forEach((k, p) => { slides[k].style.order = String(p); });
    track.style.scrollSnapType = 'none';
    track.scrollLeft = W;                       /* same slide, same place */
    paint();

    const dur = 420, t0 = performance.now(), start = W, end = (1 + dir) * W;
    const ease = p => (p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2);
    const done = () => {
      slides.forEach(sl => { sl.style.order = ''; });
      track.scrollLeft = n * W;                 /* same slide, same place, again */
      track.style.scrollSnapType = '';
      wrapping = false;
      paint();
    };
    (function run(now) {
      const p = Math.min(1, (now - t0) / dur);
      track.scrollLeft = start + (end - start) * ease(p);
      if (p < 1) tween = requestAnimationFrame(run);
      else snapTimer = setTimeout(done, 40);
    })(t0);
  }

  function goto(i, dirHint) {
    if (looping()) { pageTo(i, dirHint); return; }
    const n = (i % slides.length + slides.length) % slides.length;   /* wraps: the hero loops */
    cur = n;                    /* so crossing the breakpoint carries the right slide */
    /* Re-selecting the slide already showing restarts the dwell rather than doing
       nothing: paint() only arms on a CHANGE of index, so without this a click on the
       current dot would leave the old countdown running underneath it. */
    if (n === index()) { armBar(n); return; }
    const isWrap = i >= slides.length || i < 0;
    const to = n * track.clientWidth;
    const from = track.scrollLeft;
    cancelAnimationFrame(tween);
    clearTimeout(snapTimer);
    /* A wrap used to jump, because tweening scrollLeft from the last slide back to the
       first sweeps BACKWARDS past every slide in between — a rewind that contradicts the
       forward progress the bar just spent ten seconds telegraphing. Jumping avoided the
       rewind and cost the move: the last slide simply appeared.

       Neither is necessary. The mobile pager solves this with a ring, and a ring is just
       `order` — which works on a flex strip whether it is scrolled or translated. Put
       the destination one width FORWARD of the slide being left, scroll there, and put
       the strip back afterwards; both re-seats are visually silent because the slide on
       screen does not move across either of them. */
    if (isWrap && !reduce.matches && !document.hidden && track.clientWidth) {
      wrapRing(n, i >= slides.length ? 1 : -1);
      return;
    }
    /* rAF never fires in a hidden tab, so jump instead of starting a tween that
       would stall at frame zero. Same for reduced-motion. */
    if (reduce.matches || document.hidden || from === to || isWrap) {
      track.style.scrollSnapType = '';
      track.scrollLeft = to;
      paint();                 /* don't wait on a scroll event that may be deferred */
      return;
    }
    track.style.scrollSnapType = 'none';
    const dur = 420, t0 = performance.now();
    const ease = p => (p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2);
    (function step(now) {
      const p = Math.min(1, (now - t0) / dur);
      track.scrollLeft = from + (to - from) * ease(p);
      if (p < 1) tween = requestAnimationFrame(step);
      else snapTimer = setTimeout(() => { track.style.scrollSnapType = ''; paint(); }, 60);
    })(t0);
  }

  /* ── slide media ─────────────────────────────────────────────
     Hero 2 is a video in once-and-hold mode: it restarts from the first frame every
     time its slide becomes the active one — by timer, click, arrow or swipe — and
     holds the last frame when it reaches the end rather than looping. Slides that are
     not showing keep their video paused and rewound, so nothing plays off screen. */
  slides.forEach(sl => {
    const v = sl.querySelector('video');
    if (!v) return;
    v.loop = false;
    const last = sl.querySelector('.hc-last');
    v.addEventListener('ended', () => {
      v.pause();                                    /* the last frame stays up */
      /* and where a source-resolution still of that frame exists, cut to it: the video
         frame is 1280x720 stretched across a band twice that wide. A cut rather than a
         fade because the still is the same picture — a crossfade would just
         double-expose it. */
      if (last) last.classList.add('is-on');
    });
  });
  /* A slide can hold more than one <video>, so take the last visible one. */
  const shownVid = sl => [...sl.querySelectorAll('video')]
    .filter(v => !v.hidden && getComputedStyle(v).visibility !== 'hidden').pop();

  /* Starting a hero video is not just play(): on a phone the call has to survive being
     refused. Muted+playsinline normally autoplays anywhere, but iOS in Low Power Mode
     rejects any play() that is not inside a live user gesture — and this one never is.
     A tap on a dot runs goto(), which starts a tween, which fires scroll, which
     schedules a rAF, and only then does paint() reach media(). The activation is long
     spent by then, which is exactly why tapping a dot sometimes left a frozen frame.

     Seek only when there is something to seek: a currentTime write on an unbuffered
     video can leave it stuck in `seeking` and swallow the play that follows. */
  let pending = null;
  function start(v) {
    if (!v) return;
    if (v.readyState >= 1 && v.currentTime > 0.01) { try { v.currentTime = 0; } catch {} }
    const p = v.play();
    if (p && p.catch) p.catch(() => { pending = v; });
  }
  /* If it was refused anyway, the next real gesture anywhere on the page retries it.
     One passive capture-phase listener, and it does nothing while nothing is pending. */
  const retryPending = () => {
    if (!pending) return;
    const v = pending; pending = null;
    v.play().catch(() => {});
  };
  addEventListener('pointerdown', retryPending, { capture: true, passive: true });
  addEventListener('keydown', retryPending, { capture: true, passive: true });

  /* ── priming, which is what actually makes slide 2 reliable ──────────────
     Playing inside the tap (tapTo) only covers the case where the visitor taps a dot.
     It does nothing for the two ways slide 2 is usually reached: the dwell timer
     advancing on its own, and a swipe — where the scroll settles long after the
     touch's activation has expired. Both are script-driven plays with no gesture
     behind them, and on a phone that is exactly what gets refused.

     iOS gates this per ELEMENT, not per page: once a given <video> has been played
     from inside a real gesture, later script-driven play() calls on that same element
     are allowed for the rest of the session. So the first touch anywhere primes all
     three at once — play each, then put back whatever was not already running. It also
     starts the buffering, which is the other half of "it didn't play": hero 2 is the
     heaviest clip on the page and iOS will not spend a byte on preload="auto" until
     the visitor has touched something.

     Only the clips that are NOT on screen get primed. Touching the visible one could
     restart a finished clip for the frame or two before the pause lands, and it does
     not need this anyway: if its own autoplay was refused it is already sitting in
     `pending`, and retryPending runs from the same gesture — which plays it and
     unlocks it in one go. An off-screen clip is always parked at zero by media(), so
     pausing and rewinding it is simply putting it back where it belongs.

     Not under LEAN: priming is what starts the buffering, and on a Save-Data connection
     or a 2GB phone the clip is fetched when its slide plays, not on the first touch. */
  let primed = false;
  function primeVideos() {
    if (primed || LEAN) return;
    primed = true;
    const onscreen = shownVid(slides[index()]);
    slides.forEach(sl => sl.querySelectorAll('video').forEach(v => {
      if (v === onscreen) return;
      const p = v.play();
      const park = () => { v.pause(); if (v.readyState >= 1) { try { v.currentTime = 0; } catch {} } };
      if (!p || !p.then) { park(); return; }
      p.then(park).catch(() => { primed = false; });  /* refused — retry next touch */
    }));
  }
  addEventListener('pointerdown', primeVideos, { capture: true, passive: true });
  addEventListener('touchend', primeVideos, { capture: true, passive: true });
  addEventListener('keydown', primeVideos, { capture: true, passive: true });

  function media(i) {
    /* A hero video restarts from zero every time its slide comes up, whether the
       carousel advanced on its own or someone clicked. It is `loop=false` and pauses
       itself on `ended`, so without this a once-through video would sit on its last
       frame for every rotation after the first. */
    slides.forEach((sl, n) => {
      const shown = n === i ? shownVid(sl) : null;
      /* back to the video whenever a slide is (re)armed, so the held still never sits
         on top of a clip that has restarted underneath it */
      sl.querySelectorAll('.hc-last').forEach(e => e.classList.remove('is-on'));
      sl.querySelectorAll('video').forEach(v => {
        /* start(), not a bare play(): it guards the rewind against an unbuffered
           element and, when the play is refused, files the clip for the retry. A bare
           .catch(()=>{}) here was throwing that refusal away — the one path that most
           needed the retry was the only one not using it. */
        if (v === shown) start(v);
        else { v.pause(); if (v.readyState >= 1) { try { v.currentTime = 0; } catch {} } }
      });
    });
    /* hero 3's whitening sweep only runs while it is the slide on screen.

       .is-whitened HOLDS the finished state on the way out, and without it there is a
       visible bug: --whn is a registered property with an initial-value of 0, and the
       sweep is `forwards`, so dropping .is-revealing does not leave her teeth white — it
       snaps --whn back to 0, the after layer's mask shuts, and her day-1 teeth reappear.
       media() runs when the showing slide CHANGES, which is the start of the transition,
       so that happened while hero 3 was still on screen sliding out. A frame or two of
       un-whitened teeth, every rotation.

       Held white on the way out, back to day 1 only when the slide is armed again — so
       the reveal still plays from the beginning on every visit, which is the point of
       re-adding the class rather than leaving it on. */
    slides.forEach((sl, n) => {
      const can = !!sl.querySelector('.hw-spark');
      const on = n === i && can;
      if (on) sl.classList.remove('is-whitened');
      else if (can && sl.classList.contains('is-revealing')) sl.classList.add('is-whitened');
      sl.classList.toggle('is-revealing', on);
    });
  }

  /* The one place that knows the showing slide changed. Attributes are written every
     paint (cheap, and a no-op when unchanged), but the media and the dwell only react
     to an actual change — re-arming on every scroll event would reset the countdown
     ~25 times during a single tween and the carousel would never advance at all. */
  let activeIndex = -1;
  function paint() {
    const i = index();
    dots.forEach((d, n) => d.setAttribute('aria-selected', String(n === i)));
    if (i === activeIndex) return;
    activeIndex = i;
    media(i);
    armBar(i);
    /* both arrows stay available: the track loops, so there is no dead end */
  }

  /* Play INSIDE the gesture, before goto() hands off to a tween: this call still holds
     the tap's user activation, so the clip is already running by the time paint()
     arrives. media() then finds it playing and start() is a cheap no-op. */
  const tapTo = (n, dir) => {
    const i = (n % slides.length + slides.length) % slides.length;
    start(shownVid(slides[i]));
    goto(n, dir);
  };
  /* A dot travels the way the dots read: tapping a dot to the right of the current one
     moves right, even when the ring's short way round would be left. */
  dots.forEach(d => d.addEventListener('click', () => {
    const n = Number(d.dataset.go);
    tapTo(n, n === index() ? 0 : (n > index() ? 1 : -1));
  }));
  arrows.forEach(a => {
    const step = Number(a.dataset.step);
    a.addEventListener('click', () => tapTo(index() + step, step));
  });
  track.addEventListener('keydown', e => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const step = e.key === 'ArrowRight' ? 1 : -1;
    goto(index() + step, step);
  });

  /* ── settling a swipe ──────────────────────────────────────────────────
     A native fling ends on position 0 or 2, one off centre. Once it has actually
     stopped — and the finger is off — the ring turns so the slide it landed on is the
     middle one again, which is what re-arms both neighbours for the next swipe.
     `scrollend` is still not everywhere, so this settles on a quiet timer instead. */
  /* ── the gesture ───────────────────────────────────────────────────────────
     A threshold swipe, not a finger-follow. This is the pattern this repo already
     ships and has already tuned on a phone — js/pdp.js:1224-1238, 42px with a
     dx > 1.2*dy axis test — and adopting it rather than inventing one removes, in a
     single stroke, every risk the review ranked highest:

       * iOS caps script-driven rAF at 60Hz in most contexts while it composites native
         scroll at 120Hz on ProMotion, so a hand-rolled finger-follow can regress the
         very word "smooth". Nothing moves during the gesture here; the whole travel is
         one composited WAAPI transform.
       * No `touch-action` is claimed, so vertical scrolling past an 88svh hero — much
         the most common gesture on this page — is completely untouched, and a diagonal
         thumb arc cannot be swallowed by an axis lock that disagrees with the browser.
       * There is no drag position to recover on an interrupt, so no backwards pop.
       * There is no synthetic click to suppress, because no element is dragged.

     Both listeners are passive: neither ever calls preventDefault. */
  let tX = null, tY = null, lock = null;
  track.addEventListener('touchstart', e => {
    ensureSeated();
    tX = e.touches[0].clientX; tY = e.touches[0].clientY; lock = null;
  }, { passive: true });
  track.addEventListener('touchmove', e => {
    if (tX == null) return;
    const t = e.touches[0];
    const dx = t.clientX - tX, dy = t.clientY - tY;
    if (lock === null) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;   /* too small to judge yet */
      lock = Math.abs(dx) > Math.abs(dy) * 1.2 ? 'x' : 'y';
    }
    if (lock === 'x' && e.cancelable) e.preventDefault();
  }, { passive: false });
  track.addEventListener('touchend', e => {
    if (tX == null) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - tX, dy = t.clientY - tY;
    const was = lock;
    tX = tY = null; lock = null;
    if (was === 'y') return;                    /* the page's gesture, not ours */
    if (Math.abs(dx) < 42 || Math.abs(dx) < Math.abs(dy) * 1.2) return;
    const step = dx < 0 ? 1 : -1;
    /* i === null marks a relative step: which slide that lands on depends on where the
       carousel has got to by the time the queue drains, not on where it was when the
       finger lifted. */
    if (anim) { start(shownVid(slides[((cur + step) % N + N) % N])); pageTo(null, step); }
    else tapTo(index() + step, step);
  }, { passive: true });

  /* Desktop only in effect — the mobile transport is a transform and emits no scroll
     events — but it is what keeps the dots honest while a desktop tween runs. */
  let raf = 0;
  function schedulePaint() {
    cancelAnimationFrame(raf);
    /* rAF is frozen in a hidden tab, which would leave the dots stale */
    if (document.hidden) { paint(); return; }
    raf = requestAnimationFrame(paint);
  }
  track.addEventListener('scroll', schedulePaint, { passive: true });
  addEventListener('resize', () => {
    /* a width change moves every snap position, so re-park before painting */
    if (looping()) { ensureSeated(); paintPos(-1); }   /* the % translate re-scales itself */
    paint();
  });
  /* crossing the breakpoint retires the ring in one direction and rebuilds it in the
     other; either way the slide on screen has to stay on screen */
  LOOP.addEventListener('change', () => {
    if (looping()) { seated = false; ensureSeated(); }
    else { unring(); track.scrollLeft = cur * W(); }
    paint();
  });
  /* The first paint happens at the very END of this function, not here: it calls
     armBar, and armBar reads `holds` and `bar` from the autoplay block below. Function
     declarations hoist but const/let bindings do not, so painting here threw a
     temporal-dead-zone ReferenceError before the carousel had drawn anything. */

  /* ── autoplay ──────────────────────────────────────────────────
     One slide every DWELL, telegraphed by the active dot filling across that time.

     The fill IS the clock — one clock, so the bar and the advance cannot disagree and
     a visitor never watches a full bar sit still, or a slide change under a half-empty
     one. But it is a Web Animations object rather than a CSS animation, so this file
     owns it outright: arming, pausing, restarting the dwell and advancing are calls on
     one object whose currentTime and playState can be read back.

     As a CSS animation the same clock was only reachable through a class name and an
     animationend event, and that indirection hid two whole classes of failure. Any
     stylesheet condition that dropped the animation stopped autoplay silently, with
     nothing here able to tell. And a paused animation never finishes, so every pause
     reason was also a potential permanent stall.

     Animation time is throttled in a background tab, so the carousel pauses there on
     its own with no visibilitychange gate — a gate that, as a plain boolean, could
     latch on and never release. Because the clock is a single animation rather than an
     accumulating counter, a tab left alone for ten minutes advances exactly one slide
     on return instead of catching up on sixty.

     NOTE: there is still no visible pause control. If this hero ships to real traffic
     that is the accessibility gap to close (WCAG 2.2.2). */
  const DWELL = 10000;
  const holds = new Set();
  let bar = null;                     /* the live dwell animation, or null */

  function stopBar() { if (bar) { bar.cancel(); bar = null; } }

  /* Arms a fresh dwell on dot i. Every path that changes which slide is showing ends
     up here, so the dwell always belongs to the slide actually on screen. */
  function armBar(i) {
    stopBar();
    if (reduce.matches) return;       /* no autoplay under reduced motion */
    const dot = dots[i];
    if (!dot) return;
    bar = dot.animate(
      [{ backgroundSize: '0% 100%' }, { backgroundSize: '100% 100%' }],
      { duration: DWELL, easing: 'linear', fill: 'forwards' });
    bar.onfinish = advance;
    if (holds.size) bar.pause();
  }

  /* always forward, and on mobile that is now one width of real travel even on the
     3 -> 1 wrap, which used to be the one advance that did not move */
  function advance() { goto(index() + 1, 1); }

  function hold(reason, on) {
    on ? holds.add(reason) : holds.delete(reason);
    if (!bar) return;
    holds.size ? bar.pause() : bar.play();
  }

  /* Only KEYBOARD focus pauses. focusin also fires when a dot or arrow is clicked —
     Chrome focuses buttons on mousedown — so holding on plain focus meant a single
     click on a dot stopped autoplay for the rest of the visit, with focusout never
     arriving until the visitor clicked somewhere outside the hero entirely.
     :focus-visible is the distinction, and if a browser will not evaluate it we fail
     OPEN and keep playing: a carousel that ignores a keyboard user for ten seconds is
     a much smaller problem than one that never moves again. */
  hero.addEventListener('focusin', e => {
    let kb = false;
    try { kb = e.target.matches(':focus-visible'); } catch { kb = false; }
    hold('focus', kb);
  });
  hero.addEventListener('focusout', () => hold('focus', false));

  /* threshold 0, deliberately: a ratio threshold can never be met by an element taller
     than 1/threshold viewports, and this hero is full-bleed. At 0.25 a short window
     would have pinned the carousel for the whole visit. */
  new IntersectionObserver(es => hold('offscreen', !es[0].isIntersecting))
    .observe(hero);

  /* Reduced motion can be toggled mid-visit; re-arm so autoplay starts or stops. */
  reduce.addEventListener('change', () => armBar(activeIndex));

  /* A finish event is dispatched from the rendering loop, so a tab that has stopped
     rendering can hold a clock already in playState 'finished' with the event still
     queued. Measured that exact state here: finish() flipped the state and no event
     arrived. Normally it is delivered on the way back, but if it is ever dropped the
     carousel would sit on a full bar for the rest of the visit. Re-checking when the
     page becomes visible costs nothing and closes that gap for good. */
  addEventListener('visibilitychange', () => {
    if (!document.hidden && bar && bar.playState === 'finished') advance();
  });

  /* Seat before the first paint if the viewport is already knowable, and again on the
     next frame and on load if it was not. Idempotent, so all three are safe. */
  ensureSeated();
  requestAnimationFrame(ensureSeated);
  /* and again once webfonts and images have settled the track's width — centring on a
     clientWidth that is still growing leaves the ring half a slide out */
  /* desktop only: the mobile transport is a percentage transform, which re-scales
     with the track by itself and has nothing to re-measure */
  addEventListener('load', () => {
    if (looping()) ensureSeated();
    else track.scrollLeft = (activeIndex < 0 ? 0 : activeIndex) * W();
  });
  /* the one entry point that works under either transport — js/herodrop.js reveals a
     slide through this rather than by writing a scroll offset that mobile ignores */
  window.__hero = { goto: tapTo, index };
  paint();          /* first paint: the dots, the slide media and the first dwell */
})();

/* ── hero 1's crop follows the edit ────────────────────────────────────────
   One object-position cannot serve this clip because it contains two shots. In the
   first, the subject and the dock live at 47-97% of the frame and the left third is
   empty wall, so the window belongs hard right. The clip then CUTS to her presenting
   the brush at 37-48%, left of centre, where the same window would remove the product
   exactly as it becomes the point. So the crop changes once, on the cut.

   Driven by requestVideoFrameCallback where it exists — it is frame-accurate and only
   runs while frames are actually being presented — and by rAF where it does not. Both
   stop when the clip does, so nothing spins while the slide is off screen. */
(function heroPan() {
  const v = document.getElementById('hp-hero-clean');
  const media = v && v.closest('.hp-hero-media');
  if (!v || !media) return;
  const MOB = matchMedia('(max-width:700px)');
  /* The clip is 24fps and CUTS at frame 119.

     It used to cut at 127, and the eight frames in between — the tail of the profile
     shot, her arm still travelling up to the mirror — are no longer in the file: v35
     drops old frames 119 through 126, keeping 118 and joining it straight to 127. So
     the same edit now falls eight frames earlier, and 126.5/24 would hold the
     right-hand crop a third of a second into the shot that needs the centred one.

     Verified rather than arithmetic. Every frame across the boundary was extracted with
     ffmpeg's own 0-based index stamped into the filename, and each new frame matched
     against the old file: new 118 is old 118 at a mean difference of 1.46, new 119 is
     old 127 at 1.38 with the next best candidate three times worse. The last time this
     constant was wrong it was because extraction produced 195 images for a 190-frame
     file, and that two-frame drift was exactly the "one or two strays" it put on screen.

     Compared at the HALF frame, 117.5/24, so floating-point never decides whether
     frame 118 is before or after the boundary.

     117.5 rather than 118.5: on the device the crop was landing a frame late, so one
     frame of the new shot still played under the old right-aligned window. The clip's
     own edit is between 118 and 119 — this now moves the window on 118, half a frame
     ahead of the picture, which is the side of the boundary a crop change wants to be
     on. A frame early is invisible; a frame late is the stray. */
  const CUT = 117.5 / 24;
  const OPEN = '97% 46%', REST = '56% 46%';

  let at = null, running = false;
  function set(p) {
    if (p === at) return;
    media.style.setProperty('--hv-pos', p);
    at = p;
  }
  function apply(t) {
    if (!MOB.matches) {                    /* desktop keeps the stylesheet's crop */
      media.style.removeProperty('--hv-pos');
      at = null;
      return;
    }
    set((t == null ? v.currentTime : t) >= CUT ? REST : OPEN);
  }
  function tick(now, meta) {
    /* mediaTime is the presentation timestamp of the frame ACTUALLY on screen, so the
       crop lands on the cut frame rather than a frame either side of it */
    apply(meta && typeof meta.mediaTime === 'number' ? meta.mediaTime : null);
    if (v.paused || v.ended) { running = false; return; }
    if (v.requestVideoFrameCallback) v.requestVideoFrameCallback(tick);
    else requestAnimationFrame(() => tick());
  }
  function start() {
    if (running) return;
    running = true;
    tick();
  }
  v.addEventListener('play', start);
  ['pause', 'ended', 'seeked', 'loadedmetadata', 'emptied'].forEach(t =>
    v.addEventListener(t, () => apply()));
  MOB.addEventListener('change', () => apply());
  apply();
  if (!v.paused) start();
})();

/* ── 23:56 dock clock ────────────────────────────────────────────
   60 tick marks; the ring fills up to the current wall-clock second, so the
   dial reads as a live stopwatch rather than a decorative ring. Repaints only
   when the second actually changes, and only while the section is on screen —
   the mock's version relaid 60 elements ten times a second, forever. */
/* The morning clip ends on the brush seated back in its dock, so it holds that frame
   for five seconds before restarting — cutting straight back to the start threw the
   payoff away. There is no attribute or CSS for a pause between iterations, so `loop`
   is off and the restart is timed here.

   The hold timer is cleared when the section leaves view and re-armed when it returns,
   so a clip that finished off screen does not come back mid-hold and restart in front
   of nobody. threshold 0 rather than a ratio: a ratio can never be met by an element
   taller than 1/threshold viewports, and that has already cost this file once. */
/* ── media budget: let go of a video that is nowhere near ──────────────────
   The PDP's release pattern (pdp.js), ported. PAUSING A VIDEO DOES NOT FREE IT: a paused
   <video> that still has a src keeps its decoded frames, its buffered range and its
   whole media pipeline, and iOS counts all of that against a per-tab limit it enforces
   by killing the tab. Five clips on this page were live from load to close.

   So a clip more than a screen and a half away gets its sources taken off and its
   pipeline torn down with load() — that call is the teardown; without it the buffer
   survives. The poster stays, so nothing looks empty. Coming back within range puts the
   sources back and the clip resumes from where it was, so a loop does not visibly
   restart under the reader. Each clip's own gate below decides when it PLAYS; this only
   decides whether it is loaded at all.

   That teardown is the ONLY load() here. Measured on iOS 17: a <video> whose src is set
   fetches just its metadata, whatever preload says, but load() — even at preload
   "none" — buffers the whole clip (the 205 s film, whole; a 56 s / 10.8 MB clip in 4 s).
   So the restore puts the source back and nothing more; the play() of the gate that
   owns the clip is what fetches it, and the film never buffers until its button is
   pressed. The UV and morning clips feed from <source> children, and re-writing a
   <source>'s src does not restart resource selection (play() only restarts it from
   NETWORK_EMPTY), so they come back on the element's own src — the URL the browser had
   already picked. */
const FAR = '150% 0px';
function makeParker(v) {
  let held = null;
  return {
    get parked() { return !!held; },
    park() {
      if (held) return;
      const src = v.getAttribute('src');
      const sources = [...v.querySelectorAll('source')].map(n => ({ n, src: n.getAttribute('src') }));
      if (!src && !sources.some(s => s.src)) return;
      held = { src: src || v.currentSrc, sources, t: v.currentTime, playing: !v.paused };
      try { v.pause(); } catch {}
      v.removeAttribute('src');
      sources.forEach(({ n }) => n.removeAttribute('src'));
      try { v.load(); } catch {}
    },
    unpark() {
      if (!held) return;
      const { src, sources, t, playing } = held;
      held = null;
      sources.forEach(({ n, src: s }) => { if (s) n.setAttribute('src', s); });
      if (src) v.setAttribute('src', src);   /* no load(): see the note above */
      /* the seek waits for the metadata, which arrives on its own on iOS and with the
         first play() elsewhere — either way the clip picks up where it was */
      if (t) v.addEventListener('loadedmetadata', () => { try { v.currentTime = t; } catch {} }, { once: true });
      /* it was running when it went, so it comes back running — a play() is the one
         fetch a standby clip is allowed */
      if (playing) v.play().catch(() => {});
    },
  };
}
/* The far gate. Built BEFORE a clip's own play/pause observer, deliberately: both watch
   the same element and are delivered in creation order, so the sources are back before
   the near gate asks the clip to play. */
function farGate(el, onNear, onFar) {
  new IntersectionObserver(es => {
    if (es.some(e => e.isIntersecting)) onNear(); else onFar();
  }, { rootMargin: FAR, threshold: 0 }).observe(el);
}

/* The UV chamber clip is `muted loop playsinline` and nothing ever paused it:
   grep found an IntersectionObserver for #hp-morning-vid and none for this one. It has
   been decoding and compositing continuously from page load — while the visitor is on
   the hero, while they swipe the colourways — taxing every other animation on the page.
   Gate it the way the morning clip is gated, and release it when it is far. */
(function uvGate() {
  const v = document.getElementById('hp-uv-vid');
  if (!v) return;
  const parker = makeParker(v);
  let near = false;
  const play = () => { if (near && !parker.parked) v.play().catch(() => {}); };
  /* a jump that crossed both gates in one delivery parked this while it was still
     running, so the unpark starts it again; off screen that is this gate's call */
  farGate(v, () => { parker.unpark(); if (near) play(); else v.pause(); }, () => parker.park());
  new IntersectionObserver(es => {
    near = es[0].isIntersecting;
    if (near) play(); else v.pause();
  }, { threshold: 0 }).observe(v);
})();

(function morningHold() {
  const v = document.getElementById('hp-morning-vid');
  if (!v) return;
  /* 3s, down from 5. The clip ends on the dock lit and drying, which is the picture the
     section is making — but it is a still, and five seconds of a still reads as a video
     that has stopped rather than as a beat before it goes again. */
  const HOLD = 3000;
  v.loop = false;
  const parker = makeParker(v);
  let timer = 0, seen = true;
  const clear = () => { clearTimeout(timer); timer = 0; };
  const restart = () => {
    timer = 0;
    try { v.currentTime = 0; } catch {}
    v.play().catch(() => {});
  };
  v.addEventListener('ended', () => { clear(); if (seen) timer = setTimeout(restart, HOLD); });
  farGate(v, () => {
    parker.unpark();
    /* the markup's `autoplay` would start it the moment it has data, on screen or not;
       pausing straight after the source goes back clears that, and the gate below plays it */
    if (!seen) v.pause();
  }, () => { clear(); parker.park(); });
  new IntersectionObserver(es => {
    seen = es[0].isIntersecting;
    if (!seen) { clear(); v.pause(); return; }
    if (parker.parked) return;                    /* the far gate is about to unpark it */
    if (v.ended) { if (!timer) timer = setTimeout(restart, HOLD); }
    else if (v.paused) v.play().catch(() => {});
  }).observe(v);
})();

/* The trust bar introduces itself once: the score rises, the five stars land one at a
   time, then the count fades up under them. Armed from here rather than in the
   stylesheet so a script failure cannot leave the rating invisible.

   Gated on first view rather than literally on load, because the hero is taller than
   most viewports and this band sits under it — running it at load would mean the
   flourish had already finished by the time anyone scrolled to it. */
(function trustIntro() {
  const score = document.querySelector('.hp-score');
  if (!score) return;
  score.classList.add('t-armed');
  const io = new IntersectionObserver(entries => {
    if (!entries[0].isIntersecting) return;
    score.classList.add('is-in');
    io.disconnect();
  }, { threshold: 0.6 });
  io.observe(score);
})();

/* The customer-story card pops in on first view. Armed from here rather than in the
   stylesheet so a script failure cannot leave the testimonial invisible, and
   disconnected after the first hit because it is an entrance, not a scroll effect. */
(function quotePop() {
  const card = document.querySelector('.hp-quote-copy');
  if (!card) return;
  card.classList.add('q-armed');
  /* Fires before the card is on screen at all, not once it is well inside the frame.

     It used to wait for two thirds of the card with the viewport shrunk 8% at each end,
     which is the right instinct for a flourish and the wrong one for the thing that
     makes the testimonial legible: the card is armed to opacity 0, so every pixel of
     scrolling between the card arriving and the threshold tripping is a visitor looking
     at a blank panel where a quote should be. On a phone the card is most of a screen
     tall and enters slowly, so that gap was seconds long.

     Three attempts, and the first two failed at opposite ends. 0.66 with the root shrunk
     8% meant the card sat visible and BLANK while the visitor scrolled it into the
     middle of the screen. Growing the root 14% past its bottom edge fixed that and broke
     the other end: the entrance ran and finished while the card was still below the
     fold, so it was over before there was anything to watch. Plain threshold 0 is the
     same failure in milder form — it trips on the card's FIRST PIXEL, and on a phone
     where the card is most of a screen tall that is a single row of pixels at the very
     bottom of the window. The stars pop and the quote rises while ninety-odd percent of
     the card is still under the fold, and by the time the visitor is actually looking at
     it the animation is finished. That is the reported "we often miss it".

     A NEGATIVE BOTTOM MARGIN is the fix, and it is height-independent in a way no
     threshold can be: the root's bottom edge is pulled up so the entrance starts once
     the card's top has climbed properly into the screen — far enough in to be seen
     starting, early enough that it is still running as the card settles. Card height
     never enters into it, which is what the `tall` fallback used to be for.

     AND THE MARGIN IS NOT WHAT ENFORCES IT, because it turned out not to. A percentage
     rootMargin did not behave as the spec reads here: at both -20% and -30% the entrance
     fired with the card's top measured at 875px in an 852px viewport — below the fold
     entirely, and identical at both values, so the margin was doing nothing at all. A
     second observer built in the harness with the same options agreed to the pixel, so
     it is the browser's reading of a percentage margin on an implicit root, not this
     code. Numbers that do not move when you change them are the tell.

     So the margin is written in PIXELS, derived from innerHeight: the root's bottom edge
     is pulled up to 0.7 of the viewport, and the observer fires the moment the card's
     top crosses that line — about two thirds down, so roughly a fifth of a screen of
     blank card beforehand and the entrance still running as the card reaches the
     middle. A pixel margin is what the percentage was supposed to be and is not open to
     the same reinterpretation. It replaces a rAF watch that, once the card was "near",
     read a layout rect every frame until the line was crossed — and kept reading it if
     the visitor scrolled back up and left the card below the fold. The observer is
     rebuilt if the viewport changes shape before the card has arrived, and there is
     nothing left running after it fires. */
  const ARM_AT = 0.7;                       /* of the viewport height, measured from the top */
  let io = null;
  const arm = () => {
    if (io) io.disconnect();
    io = new IntersectionObserver(entries => {
      if (!entries.some(e => e.isIntersecting)) return;
      io.disconnect();
      io = null;
      removeEventListener('resize', arm);
      card.classList.add('is-in');
    }, { rootMargin: `0px 0px -${Math.round(innerHeight * (1 - ARM_AT))}px 0px`, threshold: 0 });
    io.observe(card);
  };
  arm();
  addEventListener('resize', arm);
})();

(function dockClock() {
  const ring = document.getElementById('dcTicksRing');
  if (!ring) return;
  const TOTAL = 60, ticks = [];
  for (let i = 0; i < TOTAL; i++) {
    const t = document.createElement('div');
    t.className = 'dc-tick' + (i === 0 ? ' dc-zero' : (i % 15 === 0 ? ' dc-major' : ''));
    t.style.transform = `rotate(${i * 360 / TOTAL}deg)`;
    ring.appendChild(t);
    ticks.push(t);
  }
  let last = -1;
  function paint(sec) {
    if (sec === last) return;
    last = sec;
    ticks.forEach((t, i) => {
      /* No `i > 0` guard. Tick 0 is the midnight tick, and excluding it from both the
         head and the fill meant the one tick that should light FIRST was the only one
         that never lit at all. At sec 0 it is the head on its own; from sec 1 it is the
         tail of the trail, which is why the ramp starts dim rather than off. */
      const lit = i < sec;
      t.classList.toggle('dc-head', i === sec);
      t.classList.toggle('dc-filled', lit);
      t.style.opacity = lit ? (0.45 + 0.55 * i / sec).toFixed(3) : '';
    });
  }
  /* reduced motion: draw the ring once, full, and leave it alone */
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) { paint(TOTAL - 1); return; }
  let timer = 0;
  /* the ring sweeps its 60 ticks in 15s — four ticks a second. The poll is a quarter
     of the step so a tick is never shown late by more than ~60ms. */
  const tick  = () => paint(Math.floor(Date.now() / 250) % TOTAL);
  const start = () => { if (!timer) { tick(); timer = setInterval(tick, 60); } };
  const stop  = () => { clearInterval(timer); timer = 0; };
  tick();                       /* never render a blank ring, even before it scrolls in */
  new IntersectionObserver(es => es[0].isIntersecting ? start() : stop(),
    { rootMargin: '120px' }).observe(ring);
})();

/* ── shades band: the timeline intro, then the wipe ──────────────
   The sweep itself is pure CSS (an animated --whn that the clip edge, the seam's
   position and its opacity all read). This does the two things CSS cannot:

   1. Measures where the chips actually are. The drawn line has to stop on the Day 7
      centre and then the Day 14 centre, and the row is three max-content columns —
      those positions are not knowable in CSS. Re-measured on resize.
   2. Sequences the two animations. The timeline introduces itself once, on first
      sight, and only then is the wipe released; both running at once reads as noise.
      The wipe also stops whenever the band leaves the screen, because a custom
      property animation restyles its subtree every frame. */
(function shadesBand() {
  const row  = document.querySelector('.hp-shades');
  const wipe = document.querySelector('.wsm');
  if (!row && !wipe) return;

  if (wipe) new IntersectionObserver(es => wipe.classList.toggle('is-idle', !es[0].isIntersecting),
    { threshold: 0.15 }).observe(wipe);
  if (!row) return;

  /* Leading edges, not centres: the line has to stop where the next bubble starts
     and wait for it, rather than crossing a bubble that is not there yet. --s3 is the
     far side of the last one, so the line only runs past Day 14 once Day 14 exists.

     Read from offsetLeft/offsetWidth rather than getBoundingClientRect, because the
     armed state holds the chips at scale(.2) and a client rect reports the SHRUNKEN
     box — which put the first stop at 36.9% when the bubble's real edge is at 44.1%.
     Layout offsets ignore transforms. The chip's offsetParent is its li, and the li's
     is the row, so the two offsets add. */
  const measure = () => {
    const W = row.offsetWidth;
    const chips = [...row.querySelectorAll('.hp-chip')];
    if (!W || chips.length < 3) return;
    const edge = (chip, side) => {
      const x = chip.parentElement.offsetLeft + chip.offsetLeft +
                (side === 'right' ? chip.offsetWidth : 0);
      return (x / W * 100).toFixed(2) + '%';
    };
    /* --s0 exists only at mobile widths, where the rails must START at Day 1's chip
       instead of leaking to the canvas edge as an orphaned stub. It must be REMOVED,
       not just skipped, above the breakpoint: the shared sh-draw keyframes subtract
       var(--s0, 0%), so a stale value left over from a resize would shorten the
       desktop line draw — the fallback is what keeps desktop bit-identical. */
    if (matchMedia('(max-width: 700px)').matches)
      row.style.setProperty('--s0', edge(chips[0], 'left'));
    else row.style.removeProperty('--s0');
    row.style.setProperty('--s1', edge(chips[1], 'left'));
    row.style.setProperty('--s2', edge(chips[2], 'left'));
    row.style.setProperty('--s3', edge(chips[2], 'right'));
  };
  measure();
  addEventListener('resize', measure);

  /* Reduced motion: leave everything in its resting state. Nothing is armed, so the
     chips and labels are simply there — the intro is decoration, not information. */
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  /* The wipe and the timeline start together: Day 1's chip pops on the same frame his
     teeth begin to whiten. It used to be held until Day 14 had landed at 1913ms, which
     put the whole intro in front of the thing the section is about.

     The hold is still what starts it, released in the same callback that lights the
     timeline. Letting the wipe simply start on its own is-idle observer instead looks
     equivalent and is not: that one watches the circle and this one watches the
     timeline row at 40%, so the two would begin on different frames. */
  row.classList.add('is-armed');
  if (wipe) wipe.classList.add('is-hold');
  const io = new IntersectionObserver(es => {
    if (!es[0].isIntersecting) return;
    io.disconnect();                      /* once, on first sight */
    measure();                            /* fonts may have settled since load */
    row.classList.add('is-lit');
    if (wipe) wipe.classList.remove('is-hold');   /* same frame as Day 1 */
  }, { threshold: 0.4 });
  io.observe(row);
})();

/* ── 99.9% counts up on first sight ──────────────────────────────
   A quick climb into the figure, once. Eased out so it decelerates onto 99.9 rather
   than stopping dead, and the element carries tabular figures in CSS so the digits
   hold their columns while it runs — otherwise the whole line shifts every frame.
   Skipped under reduced motion, where the number is simply the number: note the text
   is only reset to 0 inside the observer, so if this never runs nothing is blanked. */
(function uvCountUp() {
  const el = document.querySelector('.hp-uv-val');
  if (!el) return;
  const to = parseFloat(el.dataset.to || el.textContent);
  if (!isFinite(to)) return;
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const DUR = 900;
  const ease = t => 1 - Math.pow(1 - t, 3);
  const run = () => {
    const t0 = performance.now();
    const step = now => {
      const p = Math.min(1, (now - t0) / DUR);
      el.textContent = (to * ease(p)).toFixed(1);
      if (p < 1) requestAnimationFrame(step);
      else el.textContent = to.toFixed(1);       /* land exactly on the real figure */
    };
    requestAnimationFrame(step);
  };

  const io = new IntersectionObserver(es => {
    if (!es[0].isIntersecting) return;
    io.disconnect();
    el.textContent = (0).toFixed(1);
    run();
  }, { threshold: 0.5 });
  io.observe(el);
})();

/* ── "feel" buzzes on first sight ───────────────────────────────
   Everything about the shake is in CSS, including its own end: the two animations are
   both 1.35s so they finish together and the word settles at identity. This only
   decides when to start it, and only ever once. */
(function feelBuzz() {
  const word = document.querySelector('.hp-morning h2 .soft');
  const vid  = document.getElementById('hp-morning-vid');
  if (!word || matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  /* The buzz is cued to the picture rather than to the scroll position: in the clip she
     has the brush against her teeth from frame 59 to frame 104, and at 24fps that is
     2.458s to 4.333s. The burst is stretched to fill those 1.875s — 21 iterations of the
     same 0.09s period, against a matching 1875ms decay, so it is the same jitter and the
     same settle to zero as before, just lasting as long as she is brushing. */
  const FPS = 24, IN = 59 / FPS, OUT = 104 / FPS, SPAN = OUT - IN;
  word.style.setProperty('--vib-n', String(Math.round(SPAN / 0.09)));
  word.style.setProperty('--vib-ms', Math.round(SPAN * 1000) + 'ms');

  /* With no video there is nothing to be in time with, so leave the word still rather
     than buzzing at an arbitrary moment. */
  if (!vid) return;

  let armed = true;
  function tick() {
    const t = vid.currentTime;
    if (armed && t >= IN && t < OUT) {
      armed = false;
      /* remove, force a reflow, re-add: the same class on a second pass would not
         restart the animation on its own */
      word.classList.remove('is-buzz');
      void word.offsetWidth;
      word.classList.add('is-buzz');
    }
    if (t < IN) armed = true;              /* the clip looped — arm for the next pass */
    schedule();
  }
  /* requestVideoFrameCallback where it exists: timeupdate fires about four times a
     second, which is a quarter-second of slop on either end of a 1.9s window. It also
     stops firing on its own while the video is paused — through the five-second hold, or
     off screen — where a bare rAF loop would keep spinning. */
  function schedule() {
    if (vid.requestVideoFrameCallback) vid.requestVideoFrameCallback(tick);
    else requestAnimationFrame(tick);
  }
  schedule();
})();

/* ── the dock-clock's rule draws itself in ──────────────────────
   Same shape as the shades timeline: armed by JS so nothing is hidden if JS never
   runs, then lit once on first sight. The draw itself is CSS. */
(function dockBar() {
  const band = document.querySelector('.dock-clock');
  if (!band || !band.querySelector('.dc-bar')) return;
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  band.classList.add('is-armed');
  const io = new IntersectionObserver(es => {
    if (!es[0].isIntersecting) return;
    io.disconnect();
    band.classList.add('is-lit');
  }, { threshold: 0.35 });
  io.observe(band);
})();

/* ── retire each hero's blur-up once its own media has painted ───────────
   The placeholder is a blurred 160x90 sitting under the video. It has to come off when
   the real picture is there, and "there" means PAINTED, not fetched: readyState 2 is
   the first frame decoded, and an <img> is only trustworthy once complete AND carrying
   a natural width. Per slide, because the three arrive at different times — slide 1 is
   on screen from the first paint and slide 3 is images the browser deprioritised.

   Failing open is the rule here as everywhere on this page: any slide whose media never
   reports gets the class anyway after three seconds, because a permanent blur over a
   working video is worse than a placeholder that leaves slightly early. */
(function heroBlurUp() {
  document.querySelectorAll('.hp-hero-slide').forEach(sl => {
    const done = () => sl.classList.add('media-ready');
    const v = sl.querySelector('video');
    if (v) {
      if (v.readyState >= 2) done();
      else {
        v.addEventListener('loadeddata', done, { once: true });
        v.addEventListener('canplay', done, { once: true });
      }
    }
    const img = sl.querySelector('.hw-shot');
    if (img) {
      if (img.complete && img.naturalWidth) done();
      else img.addEventListener('load', done, { once: true });
    }
    if (!v && !img) done();
    setTimeout(done, 3000);
  });
})();

/* ── slide 2's clip and slide 3's stills are fetched a beat after load ──────
   Slide 2 shipped preload="auto" so it would be buffered by the time the dwell reached
   it at ten seconds — and it was, at the cost of 1.3MB of webm racing the LCP poster and
   the stylesheet for the first connection. Slide 3's two photographs were eager for the
   same reason. The markup now holds the clip at metadata and the stills lazy, and this
   raises them 3.5 seconds after load: the first paint has settled by then and there are
   six seconds to spare before slide 2 arrives. Under LEAN nothing is raised — the clip
   is fetched when its slide plays it, the stills when the ring brings them near. */
(function heroLateMedia() {
  const sonic = document.getElementById('hp-hero-sonic');
  const stills = [...document.querySelectorAll('.hp-hero-slide .hw-shot')];
  if ((!sonic && !stills.length) || LEAN) return;
  const raise = () => {
    stills.forEach(img => { img.loading = 'eager'; });
    if (!sonic || sonic.preload === 'auto') return;
    sonic.preload = 'auto';
    /* a parked clip with nothing decoded yet: load() is what actually starts the
       buffering on engines that treat a change of preload as advisory. Never while it
       is playing — a visitor can reach slide 2 inside 3.5 seconds with a swipe. */
    if (sonic.paused && sonic.readyState < 2) { try { sonic.load(); } catch {} }
  };
  const arm = () => setTimeout(raise, 3500);
  if (document.readyState === 'complete') arm();
  else addEventListener('load', arm, { once: true });
})();

/* ── hero 3's whitening reveal: cover geometry ───────────────────
   The shades circle can place its sparkle in plain CSS percentages because the image
   fills the frame. The hero cannot: it crops with object-fit:cover, so where her teeth
   actually land depends on the container's aspect ratio. This works out the cover
   geometry and publishes the teeth's left and right edges and their vertical centre as
   pixel values, which the CSS then slides between with --whn.

   The teeth box comes from differencing the two frames: x 57.4%-68.9%, y 44.8%-58.8%
   of the source image. */
(function heroReveal() {
  const spark = document.querySelector('.hw-spark');
  if (!spark) return;
  const media = spark.closest('.hp-hero-media');
  const img = media.querySelector('.hw-shot');
  /* Her UPPER arch only — the sweep runs across the top teeth. Measured by
     .hp/build-whiten-layer.py, which masks the difference between the darker_ and
     brighter_ renders: they are one render batch differing by a mean of 0.075 levels
     with zero differing pixels outside the mouth, so the diff IS the whitening.
     x 58.25-68.13%, y 47.13-52.62%. The lower arch (x 59.4-65.5%) sits inside that x
     range, so the wipe still uncovers every whitened tooth. */
  const TEETH = { x0: 0.583, x1: 0.681, y0: 0.471, y1: 0.526 };
  /* Read the crop off the element rather than restating it here. A hand-maintained
     copy is what let this drift to 70/38 while the stylesheet said 52/46. */
  const frac = t => t && t.endsWith('%') ? parseFloat(t) / 100 : 0.5;
  const MOB3 = matchMedia('(max-width:700px)');

  /* Her face's centre in the source plate, measured rather than eyeballed: lit facial
     skin isolated (warm, bright, red-dominant; hair and background excluded), largest
     contiguous run taken — x 1141-1585 of 2096. */
  const FACE_CX = 0.6503;

  const place = () => {
    const W = media.clientWidth, H = media.clientHeight;
    const nw = img.naturalWidth, nh = img.naturalHeight;
    if (!W || !H || !nw || !nh) return;
    /* object-fit: cover — scale to the larger ratio, then object-position picks which
       part of the overflow is trimmed */
    const scale = Math.max(W / nw, H / nh);
    const dw = nw * scale, dh = nh * scale;

    /* On a phone the crop that centres her face is COMPUTED, not written down. A fixed
       percentage only holds at the one box it was measured on, and this box is not
       fixed: the slide is clamp(600px, 88svh, 880px) tall, so its height comes from the
       viewport's HEIGHT while its width comes from the viewport's width. The aspect
       therefore changes from phone to phone, the cover crop changes with it, and 96.5% —
       correct at 393x328 — put her a long way left on a taller, wider window. Solving it
       per box is the same arithmetic done every time the box changes, which is what the
       ResizeObserver below already exists for. */
    let posx;
    const ovx = dw - W;
    if (MOB3.matches && ovx > 1) {
      posx = Math.max(0, Math.min(1, (FACE_CX * nw - (W / scale) / 2) / (ovx / scale)));
      media.style.setProperty('--hw-pos', (posx * 100).toFixed(2) + '% 40%');
    } else {
      media.style.removeProperty('--hw-pos');     /* let the stylesheet have desktop back */
      posx = frac(getComputedStyle(img).objectPosition.split(/\s+/)[0]);
    }
    const op = getComputedStyle(img).objectPosition.split(/\s+/);
    const POS = { x: posx, y: frac(op[1] || op[0]) };
    const ox = (W - dw) * POS.x, oy = (H - dh) * POS.y;
    media.style.setProperty('--tx0', (ox + TEETH.x0 * dw).toFixed(1) + 'px');
    media.style.setProperty('--tx1', (ox + TEETH.x1 * dw).toFixed(1) + 'px');
    media.style.setProperty('--ty',  (oy + (TEETH.y0 + TEETH.y1) / 2 * dh).toFixed(1) + 'px');
    /* 0.72 of the arch height — back to the ratio the shades circle uses, because
       bigger was making it LESS visible, not more.

       The glint is masked by the after layer's alpha, which is exactly her teeth. Her
       upper arch is 26px tall in the mobile crop. At 2.5 the bloom was 132px — five
       times the height of the thing it is allowed to appear on — so almost all of it was
       cut away and what survived was a hard-edged white slab across her teeth with the
       four-point star completely swamped inside it. That is what read as occluded, and
       it is also why 1.25 (2.5x the arch height) already looked like a haze rather than
       a sparkle rather than looking small.

       A sparkle reads as a sparkle when the bloom is compact enough that its arms show.
       The circle is 1.44x its own arch height and is the one that looks right, so this
       is the same: 0.72 x arch, bloom 38px on a 26px arch. Hers comes out smaller than
       his 46px in absolute terms because her arch is smaller than his — that is
       proportion, not a mistake.

       Obviousness comes from brightness instead, which is the axis that was actually
       available: see .hw-spark::before/::after, now full-white cored with thicker arms.
       Size is bounded by her teeth and cannot buy it. */
    /* 0.81, not 0.72. Both glints are now unclipped, so their drawn diameters can be
       compared directly against the teeth they sit on — which is the ratio the eye
       actually judges. His is 51.8% of his teeth width; at 0.72 hers was 46.0%. 0.81
       is 0.72 x (51.8 / 46.0) and lands hers on the same figure. */
    media.style.setProperty('--sp', Math.round((TEETH.y1 - TEETH.y0) * dh * 0.81) + 'px');
    /* The sparkle is masked by the after layer's alpha (her teeth), so that mask has to
       be drawn at the same size and offset the cropped photo is. object-fit:cover does
       this for the <img>; a mask-image has to be told. */
    media.style.setProperty('--mw', dw.toFixed(1) + 'px');
    media.style.setProperty('--mh', dh.toFixed(1) + 'px');
    media.style.setProperty('--mx', ox.toFixed(1) + 'px');
    media.style.setProperty('--my', oy.toFixed(1) + 'px');
  };

  if (img.complete && img.naturalWidth) place();
  else img.addEventListener('load', place, { once: true });
  /* A ResizeObserver on the media box, not a window resize listener: the hero's height
     changes without the window doing anything — the sticky bar arriving, webfonts
     landing, the copy column reflowing — and every published value here is derived from
     that box. A stale --ty leaves the sparkle off the arch. */
  new ResizeObserver(place).observe(media);
  MOB3.addEventListener('change', place);
})();

/* ── the colourway reel ─────────────────────────────────────────────────
   Two jobs, both mobile-only.

   Warming: the tiles are `loading="lazy"`, which on a phone means the first swipe of
   the row is a swipe into empty boxes. An observer two screens out flips them to eager
   and mirrors each into a detached Image, so by the time the section arrives the
   pictures are already decoded and the row is solid from its first frame.

   The spin: a row that happens to be wider than the screen looks like a row that got
   cut off. So the first time it comes into view it takes a flick — real momentum with
   friction, not a canned easing — and coasts three colourways in before settling. The
   motion is the affordance: nothing else on this page says "you can push this".

   Integrated against elapsed time rather than per frame, so a 120Hz phone gets the
   same 1.3 seconds as a 60Hz one instead of half of it. Any touch cancels it outright,
   and having already touched the row cancels it before it starts — a visitor who has
   found the affordance does not need to be shown it. */
/* ── the film ────────────────────────────────────────────────────────────
   The PDP's about-video section, ported. Same clip, same three behaviours: a preview
   slideshow that crossfades hand-picked frames while the film is idle, an ambilight
   canvas that paints a downscaled copy of whatever is showing so the frame glows in its
   own colours, and a play button that hands over to the real video with sound.

   It is a port, not a shared module: js/main.js owns the PDP's copy and this page does
   not load main.js. What is carried across is the reasoning, most of it in ambLoop —
   see the note there, which is the part that is easy to get wrong.

   Scoped to this section by element, so it cannot collide with the PDP's ids if the two
   ever share a bundle. */
(function storyFilm() {
  const frame = document.getElementById('hp-film');
  if (!frame) return;
  const vid = document.getElementById('hp-film-vid');
  const amb = frame.querySelector('.ambilight');
  const a = frame.querySelector('.vc-a'), b = frame.querySelector('.vc-b');
  const playBtn = document.getElementById('hp-film-play');
  if (!vid || !amb || !a || !b || !playBtn) return;
  const slow = matchMedia('(prefers-reduced-motion: reduce)');
  /* the film is released when it is far, after it ends, and never while it plays */
  const parker = makeParker(vid);
  let autoPaused = false;

  /* hand-picked frames of the film, in a hand-picked order */
  const ORDER = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 18, 19, 21, 25, 23, 24, 26, 27, 28, 29, 30, 31, 32];
  const SHOTS = ORDER.map(n => `https://cdn.shopify.com/s/files/1/1009/8326/files/tao-mockup--video--preview--preview-${n}.jpg?v=5`);
  const warm = new Map();
  let idx = 0, front = a, timer = null, loading = false, inView = false, started = false;
  /* ONE PASS. The cycle used to run for as long as the section was on screen — a JPEG
     every three seconds, forever, for anyone who left the page open on it. It now
     crossfades through the set once and holds the last frame; every crossfade in that
     pass looks exactly as it did. */
  let lapped = false;

  const preload = i => {
    if (i >= SHOTS.length) return;               /* the pass ends on the last frame */
    const src = SHOTS[i];
    if (warm.has(src)) return;
    const im = new Image(); im.src = src; warm.set(src, im);
  };
  function first() {
    if (started) return;
    started = true;
    a.src = SHOTS[0]; a.classList.add('on');
    preload(1);
  }
  /* Swap only once the NEXT frame has decoded. Setting src and adding the class in the
     same breath crossfades to an empty element on a slow connection. */
  function next() {
    if (loading) return;
    first();
    const n = (idx + 1) % SHOTS.length;
    const showing = front, hidden = front === a ? b : a;
    loading = true;
    let done = false;
    const reveal = () => {
      if (done) return;
      done = true; loading = false;
      if (!inView || frame.classList.contains('playing')) return;
      idx = n;
      hidden.classList.add('on');
      showing.classList.remove('on');
      front = hidden;
      if (n === SHOTS.length - 1) { lapped = true; stop(); return; }
      preload(n + 1);
    };
    hidden.onload = reveal;
    hidden.onerror = () => { if (!done) { done = true; loading = false; } };
    hidden.src = SHOTS[n];
    if (hidden.complete && hidden.naturalWidth) reveal();
  }
  const start = () => {
    if (timer || lapped || !inView || document.hidden || slow.matches) return;
    first();
    timer = setInterval(next, 3000);
  };
  const stop = () => { clearInterval(timer); timer = null; };

  /* far: the clip's pipeline goes, whether it was played or not. Near: it comes back,
     at the time it was at; whether it then plays is the in-view gate's call. A flick
     can cross both gates in one delivery, and this one runs first — so it is the one
     that has to notice a film still playing, before park() pauses it. */
  /* A film the visitor is watching with sound is theirs — it may be in
     picture-in-picture while they read — so neither gate touches it. */
  const watching = () => !vid.paused && !vid.muted && frame.classList.contains('playing');
  farGate(frame, () => parker.unpark(), () => { if (watching()) return; if (!vid.paused) autoPaused = true; parker.park(); });
  new IntersectionObserver(es => {
    inView = es.some(e => e.isIntersecting);
    if (inView && !frame.classList.contains('playing')) start(); else stop();
    if (inView) startAmb(); else stopAmb();
    /* A film scrolled away from pauses — a clip with sound running under the rest of
       the page is worse than a pause — and resumes when the visitor scrolls back to
       it, from where it was. If that play() is refused, the controls are up. */
    if (!frame.classList.contains('playing')) return;
    if (!inView && !vid.paused && !watching()) { vid.pause(); autoPaused = true; }
    else if (inView && autoPaused) { autoPaused = false; parker.unpark(); vid.play().catch(() => {}); }
  }, { rootMargin: '220px 0px', threshold: 0 }).observe(frame);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) { stop(); stopAmb(); return; }
    if (inView && !frame.classList.contains('playing')) start();
    startAmb();
  });

  /* ── the ambilight ── */
  const ctx = amb.getContext('2d');
  let last = 0, ambRaf = 0;
  const size = (w, h) => {
    const bw = 160, bh = Math.max(1, Math.round(bw * h / w));
    if (amb.width !== bw || amb.height !== bh) { amb.width = bw; amb.height = bh; }
  };
  const draw = src => {
    const w = src.videoWidth || src.naturalWidth, h = src.videoHeight || src.naturalHeight;
    if (!w || !h) return;
    size(w, h);
    ctx.globalAlpha = 1;
    try { ctx.drawImage(src, 0, 0, amb.width, amb.height); } catch (e) {}
  };
  function loop(t) {
    ambRaf = requestAnimationFrame(loop);
    if (t - last < 33) return;
    last = t;
    if (!amb.offsetParent) return;

    /* the video layer is up — keyed off the class, not vid.paused, because a film
       paused mid-way is still the thing on screen */
    if (frame.classList.contains('playing') && vid.readyState >= 2 && vid.videoWidth) {
      draw(vid); return;
    }

    /* Otherwise reproduce the DOM's own compositing exactly: both <img> layers over the
       black frame, each at its LIVE css opacity. That is what keeps the halo fading in
       step with the pictures instead of staying lit and flaring past them. Because the
       two opacities sum to 1 at every instant of the crossfade, the midpoint shows about
       25% black — matching the frames rather than over-brightening between them. */
    const dim = a.naturalWidth ? a : (b.naturalWidth ? b : null);
    if (!dim) { if (vid.readyState >= 2 && vid.videoWidth) draw(vid); return; }
    size(dim.naturalWidth, dim.naturalHeight);
    ctx.globalAlpha = 1;
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, amb.width, amb.height);
    for (const im of [a, b]) {                    /* DOM paint order: a under b */
      if (!im.complete || !im.naturalWidth) continue;
      ctx.globalAlpha = Math.min(1, Math.max(0, parseFloat(getComputedStyle(im).opacity) || 0));
      try { ctx.drawImage(im, 0, 0, amb.width, amb.height); } catch (e) {}
    }
    ctx.globalAlpha = 1;
  }
  /* The loop runs only while there is something to glow for: the section inside its
     margin and the tab visible. It used to run from load until the page was closed —
     a getComputedStyle read and up to two drawImage calls thirty times a second for a
     canvas that was off screen for most of the visit. Reduced motion skips it
     outright: the slideshow it mirrors does not run there either. The in-view observer
     and visibilitychange above are the only callers. */
  function startAmb() {
    if (ambRaf || !inView || document.hidden || slow.matches) return;
    ambRaf = requestAnimationFrame(loop);
  }
  function stopAmb() { cancelAnimationFrame(ambRaf); ambRaf = 0; }

  playBtn.addEventListener('click', () => {
    frame.classList.add('playing');
    stop();
    autoPaused = false;
    parker.unpark();                              /* a no-op unless it was released */
    vid.controls = true;
    vid.muted = false;
    vid.volume = 1;
    vid.play();
  });
  vid.addEventListener('ended', () => {
    frame.classList.remove('playing');
    vid.controls = false;
    autoPaused = false;
    /* Let the whole film go: a finished clip held forty-odd seconds of buffer for the
       rest of the visit. Rewound first, so the release remembers 0 rather than the end
       and a second press starts from the top instead of ending on arrival. The preview
       layer is fading back over it, so neither the seek nor the poster shows. */
    try { vid.currentTime = 0; } catch {}
    parker.park();
    if (inView) start();
  });
})();

/* ── the ritual band's UV pulse ──────────────────────────────────────────
   A burst of ultraviolet across the word "ritual": a lens-flare core, an anamorphic
   streak, a spark burst on canvas, and the letters lifting in sequence. The CSS carries
   everything except the sparks, which need real particles.

   Three things differ from a straight port. The canvas is sized to the WORD's own box
   and to devicePixelRatio rather than to a fixed 800x400 — this headline is
   clamp(44px,7.4vw,108px), so a fixed canvas is right at one width and wrong at every
   other. The rAF loop runs only while particles are alive instead of forever, because a
   permanent loop on a page that already drives two pagers and four videos is a tax paid
   for nothing between bursts. And the first pulse is fired when the band comes into
   view rather than on load, which is where it can actually be seen — the band is the
   last thing before the footer. */
(function ritualUV() {
  const band = document.getElementById('hp-ritual');
  if (!band) return;
  const wrap = band.querySelector('.uv-wrap');
  const cv = band.querySelector('.uv-spark');
  if (!wrap || !cv) return;
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const ctx = cv.getContext('2d');
  const TINT = ['#00f3ff', '#a100ff', '#8a2be2', '#ffffff', '#3a00e5'];
  let dpr = 1, W = 0, H = 0;

  /* the spark field is the word's box grown by half again either way, so a burst can
     leave the letters without being clipped by its own canvas */
  function size() {
    const r = wrap.getBoundingClientRect();
    if (!r.width) return false;
    dpr = Math.min(2, devicePixelRatio || 1);
    W = Math.round(r.width * 2.2); H = Math.round(r.height * 3.2);
    cv.style.width = W + 'px'; cv.style.height = H + 'px';
    cv.width = Math.round(W * dpr); cv.height = Math.round(H * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    return true;
  }

  let parts = [], raf = 0;
  function spawn(n) {
    const r = wrap.getBoundingClientRect();
    const spanX = r.width * 0.62, spanY = r.height * 0.18;
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = Math.random() * 4.5 + 1.5;
      parts.push({ x: W / 2 + (Math.random() - .5) * spanX,
                   y: H / 2 + (Math.random() - .5) * spanY,
                   vx: Math.cos(a) * sp, vy: Math.sin(a) * sp,
                   r: Math.random() * 2.8 + 0.6, life: 1,
                   decay: Math.random() * 0.038 + 0.018,
                   c: TINT[(Math.random() * TINT.length) | 0] });
    }
    if (!raf) raf = requestAnimationFrame(tick);
  }

  function tick() {
    ctx.clearRect(0, 0, W, H);
    for (let i = parts.length - 1; i >= 0; i--) {
      const p = parts[i];
      p.x += p.vx; p.y += p.vy; p.vx *= .94; p.vy *= .94; p.life -= p.decay;
      if (p.life <= 0) { parts.splice(i, 1); continue; }
      ctx.save();
      /* additive inside the canvas rather than mix-blend-mode on it — same glow, no
         extra compositing layer over the word. See the note on .uv-spark. */
      ctx.globalCompositeOperation = 'lighter';
      ctx.globalAlpha = p.life;
      ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
      ctx.fillStyle = p.c; ctx.shadowColor = p.c; ctx.shadowBlur = 10;
      ctx.fill(); ctx.restore();
    }
    /* stop dead when the last spark dies, rather than idling at 60fps forever */
    raf = parts.length ? requestAnimationFrame(tick) : 0;
  }

  let busy = false, lastTap = 0;
  const COOLOFF = 900;                       /* between deliberate taps */
  function pulse(force) {
    /* A COOL-OFF, so a fat-fingered double tap fires once. This is a different guard
       from `busy`: busy stops a hover storm restarting the CSS mid-flash and a real tap
       is allowed to beat it, which is what `force` is for. Without something else, that
       made every stray second tap re-trigger. The cool-off is measured tap-to-tap, so
       one deliberate press still always works and the second one inside a second does
       not. */
    if (force) {
      const now = performance.now();
      if (now - lastTap < COOLOFF) return;
      lastTap = now;
    }
    /* A deliberate tap always wins. `busy` exists to stop a hover storm restarting the
       CSS mid-flash; it should never make a visitor's tap do nothing, which it did if
       they landed within 1.15s of the entrance pulse. */
    if ((busy && !force) || document.hidden) return;
    if (!W && !size()) return;
    busy = true;
    band.classList.remove('is-uv');
    void band.offsetWidth;                     /* restart the CSS, do not queue it */
    band.classList.add('is-uv');
    spawn(65);
    /* Take the class OFF when the burst is over. The reference leaves it on, which
       leaves the word permanently lit — a flash that ends as a standing neon sign is
       not a flash, and it also means a second hover has nothing to add. .uv-word carries
       a colour and text-shadow transition, so dropping the class eases the glow back to
       cream italic instead of cutting it. */
    setTimeout(() => { band.classList.remove('is-uv'); busy = false; }, 1150);
  }

  addEventListener('resize', () => { if (W) size(); });
  /* ── why a tap needed two goes ────────────────────────────────────────────
     pointerenter is a MOUSE affordance and this was firing it for touch as well. On iOS
     the first tap on something that has a hover state is spent ENTERING that state —
     the click is swallowed and only the second tap dispatches one. That is exactly the
     reported "you have to select it and then hit it".

     pointerdown has none of that: it fires on the first contact, before any hover
     round-trip, and it cannot be swallowed. So touch gets pointerdown, a mouse keeps
     pointerenter, and click stays for the keyboard and for anything that reaches it by
     another route. `force` means the tap cannot lose a race with the entrance flash. */
  wrap.addEventListener('pointerenter', e => { if (e.pointerType !== 'touch') pulse(); });
  wrap.addEventListener('pointerdown', e => { if (e.pointerType === 'touch') pulse(true); });
  wrap.addEventListener('click', () => pulse(true));

  /* Fire on the WORD, not on the band, and only once the word has risen clear of the
     bottom quarter of the screen. Watching the band at 45% meant the flash could go off
     while "ritual" itself was still below the fold — the band is far taller than the
     line, so 45% of it says nothing about whether the thing that flashes is visible.
     The negative bottom margin is height-independent in a way a threshold is not. */
  new IntersectionObserver((es, o) => {
    if (!es.some(e => e.isIntersecting)) return;
    o.disconnect();
    size();
    setTimeout(pulse, 260);
  }, { rootMargin: '0px 0px -25% 0px', threshold: 0 }).observe(wrap);
})();

/* ── the colourway rows ──────────────────────────────────────────────────
   One transport, instantiated per row. It was a single IIFE closed over one grid; two
   rows drifting against each other need two of everything except the reasons to stop —
   a cursor is over the SECTION, not over a row, and pausing one row while the other
   walked on would look like a fault. So the gates are shared and the physics are not. */
(function roomsReels() {
  const section = document.querySelector('.hp-rooms');
  if (!section) return;
  const slow = matchMedia('(prefers-reduced-motion: reduce)');
  /* The reasons to STOP are shared; the physics are not. A cursor over one row pauses
     BOTH, because pausing one while the other walked on would read as a fault rather
     than as a pause. */
  const shared = {
    hovering: false, inView: false,
    /* Apply something to every row: `self` to the one the gesture is on, `other` to the
       rest. This is what ties the two rows together — every gesture goes through here,
       and `other` is always the mirror of `self`. */
    spread(self, onSelf, onOther) { reels.forEach(r => (r === self ? onSelf : onOther)(r)); },
  };
  const reels = [];

/* Build an IntersectionObserver, start it on the section, and hand it back so the
   reel's teardown can disconnect it with the rest. */
function observe(fn, opts, section) {
  const io = new IntersectionObserver(fn, opts);
  io.observe(section);
  return io;
}

function makeReel(grid, dir, section, slow, shared) {
  /* One signal for every listener and observer below, so the whole reel can be taken
     down in one call. That matters because the ROW ITSELF is not permanent any more: the
     phone has one row and the desktop has two, the tiles move between grids when the
     viewport crosses the breakpoint, and a reel still holding the tiles it captured at
     construction would be driving elements that now belong to the other row. */
  const ac = new AbortController();
  const sig = { signal: ac.signal };
  const obs = [];
  /* The row runs at every width now. It was mobile-only because desktop had a grid
     wall, and a wall and a pager cannot both own the same element — so the pager stood
     down above 700px and unring() put the wall back. Desktop is the row too, so there is
     no longer a breakpoint to stand down at, and the teardown path goes with it. */
  const rowMode = () => true;
  const tiles = [...grid.querySelectorAll('.hp-room')];
  const N = tiles.length;
  if (!N) return;
  const C = N >> 1;                              /* the centre seat: 6 of 13 */

  /* ── why this is a transform pager and not a scroll container ──────────────
     It was a native scroll-snap row with a ring of clones for runway, and the runway
     was the bug. A ring can only turn while the row is STILL, so every re-seat is a
     promise that the row will hold still soon enough — and on a phone it does not.
     Thirteen colourways and thirteen tiles of runway meant that swiping through the
     whole set landed exactly on the strip's hard end, which is the pause: the last
     photo, nothing to scroll into, and a wait while the ring finally caught up.

     Lengthening the strip only moves the wall. What removes it is having no strip:
     the row is translated rather than scrolled, so the re-seat is a synchronous style
     write with no precondition at all and there is no offset to run out of. The hero
     took this shape first and is proven on the device; this is the same machine with
     N=13 instead of 3, so the two now fail and succeed together.

     It also deletes the twenty-six clones, and with them two thirds of the raster this
     section asked a phone to hold. */

  let ring = null, seated = false, cur = 0, pos = C, dragFrom = C;
  /* Set by destroy() and checked by everything that can be reached AFTER it — a timer
     or a frame already queued when the breakpoint re-dealt the tiles. A dead reel that
     still writes `order` and a transform is writing onto tiles another reel now owns.
     Every handle a teardown has to clear lives here too, next to the flag. */
  let destroyed = false;
  let seatRaf = 0, idleHandle = 0, readyTimer = 0, readyRace = 0;

  /* ── the physics ──────────────────────────────────────────────────────────
     The row used to move by keyframe animation: one pitch, a fixed 340ms, a fixed
     easing, and a queue to hold gestures that arrived while it was busy. That transport
     cannot follow a finger, and a row you push that does not move under your thumb reads
     as a list of buttons rather than as something physical.

     So it is integrated instead. `pos` is where the row is in pitch units, `vel` is how
     fast it is going in pitch/ms, and every frame applies a spring toward `target` plus
     a damping term. K and Cd are tuned rather than picked: simulated across the range,
     this pair settles a one-tile move in about 280ms with 13% of overshoot, which is the
     bounce at the end of a flick. Critically damped would be 0% and would feel like the
     keyframes it replaced.

     There is no travel limit. normalise() turns the ring under whatever is moving, so a
     drag, a coast and the entrance spin can all run as far as they like; a ring has no
     end and pretending otherwise with a rubber band would be a lie about it. */
  const K = 0.00085;          /* spring, per ms^2 — the settle */
  /* 0.038. Simulated across the range: this overshoots a one-tile move by 1.4% and
     settles in 200ms. 0.028 was 13.2% (a bounce you watch happen), 0.034 was 4.8% (still
     a visible rebound at the end of a spin), and 0.044 is 0.0% — no elastic at all.
     1.4% is a landing that gives slightly rather than one that springs. */
  const Cd = 0.038;           /* damping, per ms */
  const FRICTION = 0.998;     /* per ms — the coast. iOS's own scroll rate. */
  const VSTOP = 0.0005;       /* pitch/ms below which a coast is over */
  /* The finger's own velocity, amplified. A coast covers vel / -ln(FRICTION) tiles, so
     without this an ordinary flick of 0.005 pitch/ms reaches 2.5 tiles and reads as
     stiff — you push it and it moves not much further than you pushed. 1.8 puts that
     same flick at 4.5, and a firm one past a whole cycle, while a slow release still
     just settles because VSTOP has not been cleared. */
  const FLICK = 1.8;
  /* Half a second at speed, half a second stopping. SPINV is not round because the
     TRAVEL is what matters: 0.0072 x 500 cruising plus 0.0072 x 500 / 2.6 decelerating
     is 5.0 tiles, and five is the most the entrance can move without normalise() having
     to turn the ring underneath it. Six tiles of runway either side, so the whole
     entrance now happens inside one seating — no `order` writes at all while it runs. */
  const SPINV = 0.0072;
  const CRUISE = 500, DECEL = 500;
  let vel = 0, raf = 0, target = C, lastT = 0, dragging = false;
  let mode = 'snap', spinT = 0;
  /* DESKTOP DRIFTS. A row wider than the screen has to say so. On a phone it is under a
     thumb that already knows; on a desktop it was arrows, and an arrow is a thing you
     have to notice and then decide to press — a row that is already moving has said it
     before you look at it.

     0.00016 pitch/ms is one tile about every six seconds: slow enough to read a
     photograph while it crosses, fast enough that nothing looks stuck. It is the RESTING
     state up here rather than an animation that plays once, so a flick coasts and then
     rejoins it instead of snapping to a tile — settling and then creeping again reads as
     two decisions where drifting is one. */
  const DRIFT = 0.00016;
  const canDrift = () => innerWidth > 700 && !slow.matches;
  /* Not off screen (nothing to say to nobody), not in a hidden tab, and not under a
     pointer — the tiles are links, and a link that walks away from the cursor is a link
     you cannot click. */
  const driftOK = () => canDrift() && shared.inView && !shared.hovering && !document.hidden && !dragging;

  let gPitch = 0, gW = -1;
  const pitch = () => {
    if (gPitch && gW === grid.clientWidth) return gPitch;
    const l = tiles.map(t => t.offsetLeft).sort((a, b) => a - b);
    gW = grid.clientWidth;
    gPitch = l.length > 1 ? l[1] - l[0] : 0;
    return gPitch;
  };
  const invalidate = () => { gPitch = 0; gW = -1; };

  /* the ONLY writer of the row's transform. The tile at visual position p is centred
     because the row carries 13vw of start padding and each tile is 74vw: 13 + 37 = 50,
     so position 0 is already centred and every other is one pitch further along. */
  function paintPos(p) {
    const q = pitch();
    if (!q || !Number.isFinite(p)) return;
    pos = p;
    grid.style.transform = 'translate3d(' + (-p * q).toFixed(2) + 'px,0,0)';
  }

  const setRing = order => { ring = order; order.forEach((k, p) => { tiles[k].style.order = String(p); }); };
  const seatRing = k => setRing(Array.from({ length: N }, (_, i) => (k - C + i + N * 2) % N));

  function ensureSeated() {
    if (destroyed || !rowMode() || seated || !pitch()) return;
    /* the resting SEQUENCE is the stylesheet's — three colourways are promoted to the
       front there by `order` — so read it back rather than restate it */
    const seq = tiles.map((el, i) => ({ i, o: parseInt(getComputedStyle(el).order, 10) || 0 }))
      .sort((a, b) => a.o - b.o || a.i - b.i).map(x => x.i);
    cur = seq[1];                                /* the second tile of that sequence */
    seatRing(cur);
    paintPos(C);
    seated = true;
    /* low, not high: this runs at module init, and the five tiles the seat just put on
       screen must not be allowed to queue in front of the hero the visitor is looking
       at. Starting the fetch early is the point; winning the race is not. warm() raises
       them the moment the section is within two screens. */
    prime('low');
  }
  function unring() {
    halt();
    tiles.forEach(t => { t.style.order = ''; });
    grid.style.transform = '';
    seated = false;
    pos = C;
  }

  /* Motion blur, driven by how fast the row is actually going rather than by a
     timeline. A CSS blur, not an SVG directional one: the row's box is thirteen tiles
     wide, and an SVG filter region that size means a per-frame raster of nearly 4000px
     on a phone. The filter comes OFF entirely below half a pixel, so an idle row carries
     no filter at all and nothing stays promoted while nothing is moving. */
  /* No motion blur. It was here to sell speed and it cost more than it bought: a CSS
     filter promotes a row of thirteen 1100px photographs to its own layer, and taking
     the filter off again at the end of a move is a whole-row sharpening step — which is
     what the unplaceable flicker after the entrance turned out to be. Lowering the
     removal threshold hid it; not having a filter at all removes the mechanism. The
     speed reads from the travel and the settle, which is where it should read from. */

  function halt() {
    if (raf) { cancelAnimationFrame(raf); raf = 0; }
    vel = 0; dragging = false;
  }

  /* The row has come to rest: fold however many tiles it travelled back into the ring
     and put the transform back on the seat. One rebuild per gesture, not one per tile —
     thirteen `order` writes relayout the row, and doing that mid-flight is exactly what
     six tiles of runway exist to avoid. */
  function land() {
    const moved = Math.round(pos - C);
    cur = ((cur + moved) % N + N) % N;
    seatRing(cur);
    paintPos(C);
    vel = 0; raf = 0; mode = 'snap'; spinT = 0;
    rasterise();
    resume();
  }

  /* Keep the row near its seat while it travels. Six tiles of runway either side is
     plenty for a settle and nowhere near enough for a two-cycle spin, so the ring turns
     mid-flight — but only every third tile, because thirteen `order` writes relayout the
     row and doing that per tile at speed is the one thing that would cost frames. Three
     is the largest step that still leaves three tiles of cover on the leading side. */
  /* KEEP is how many tiles of runway must remain beyond the last visible one. Thirteen
     tiles with the centre seat at 6 leaves six either side, and the viewport shows about
     1.4 of them on a phone but five or so on a desktop — so the desktop row is already
     using 2.5 of that six before anything moves. Re-seating every third tile left three
     in reserve, which was ample at 1.4 visible and marginal at 5. */
  function normalise() {
    const KEEP = innerWidth > 700 ? 2 : 3;
    const off = Math.round(pos - C);
    if (Math.abs(off) < KEEP) return;
    cur = ((cur + off) % N + N) % N;
    seatRing(cur);
    pos -= off;
    target -= off;
  }

  function frame(now) {
    const dt = Math.min(34, now - lastT || 16);
    lastT = now;

    if (mode === 'spin') {
      /* Cruise at speed, then ramp the speed itself down to zero over DECEL — a ramp
         rather than a friction constant because it has to ARRIVE at a stop on time,
         and friction only ever approaches one. */
      spinT += dt;
      if (spinT <= CRUISE) vel = SPINV * dir;
      else {
        const p = Math.min(1, (spinT - CRUISE) / DECEL);
        vel = SPINV * dir * Math.pow(1 - p, 1.6);
        if (p >= 1) { mode = 'snap'; target = Math.round(pos); }
      }
      pos += vel * dt;
    } else if (mode === 'drift') {
      if (!driftOK()) { raf = 0; vel = 0; return; }
      vel = DRIFT * dir;
      pos += vel * dt;
    } else if (mode === 'fling') {
      /* Pure coast. The velocity the finger left is the velocity it starts with, so a
         harder flick genuinely goes further — the distance is v / -ln(FRICTION), which
         is linear in the flick. Nothing is rounded until it has almost stopped, so the
         row never snaps out from under the gesture. */
      vel *= Math.pow(FRICTION, dt);
      pos += vel * dt;
      if (Math.abs(vel) < VSTOP) {
        if (driftOK()) mode = 'drift';
        else { mode = 'snap'; target = Math.round(pos); }
      }
    } else {
      vel += ((target - pos) * K - vel * Cd) * dt;
      pos += vel * dt;
    }

    normalise();
    paintPos(pos);

    if (mode === 'snap' && Math.abs(target - pos) < 0.004 && Math.abs(vel) < 0.00008) {
      paintPos(target); land(); return;
    }
    raf = requestAnimationFrame(frame);
  }

  function run(m) {
    if (destroyed) return;
    mode = m;
    if (slow.matches || document.hidden || !pitch()) {
      pos = target = Math.round(pos); paintPos(pos); land(); return;
    }
    if (raf) return;                      /* already running — it picks up the new mode */
    lastT = performance.now();
    raf = requestAnimationFrame(frame);
  }

  function springTo(t) { target = t; run('snap'); }

  /* The one way back into the drift. Called whenever something that could have stopped it
     changes — a settle finishing, the row scrolling into view, a cursor leaving, the tab
     coming back — and a no-op unless the drift is both wanted and not already running, so
     every caller can call it without checking first. */
  function resume() {
    if (destroyed || !driftOK() || raf) return;
    mode = 'drift';
    lastT = performance.now();
    raf = requestAnimationFrame(frame);
  }


  /* One tile of travel. A gesture arriving mid-settle no longer queues: it moves the
     target, so a hurried visitor gets one continuous accelerating move rather than a
     tail of separate settles. */
  function step(dir) {
    ensureSeated();
    if (!seated) return;
    springTo((raf ? target : pos) + dir);
  }

  /* The five seats: the tiles on screen and one either side.

     They stop being candidates for lazy loading the instant the row seats, and that is
     the whole bug behind a row that paints as a band of empty boxes. Seating translates
     the row six pitches in one synchronous write, so the tiles that land on screen are
     ones the browser never saw anywhere near the viewport and therefore never fetched —
     `loading="lazy"` had already made its decision about them, and it decided no. The
     warming observer two screens out normally hides this, but a load that arrives
     already scrolled to the section gets no head start at all, and then the row is a
     hole until the fetches land.

     Priority is the second half of it. Warming used to mark every tile `low`, seats
     included, so the pictures the visitor is actually looking at queued behind
     everything else on the page. Seats are `high` from the moment the section is worth
     warming; at boot they are `low`, so starting them early cannot cost the hero.

     And `complete` only means the bytes arrived: a phone under memory pressure drops the
     rasterised copy for anything far from the viewport, and rebuilding one at the moment
     it becomes the next photograph is a visible stall. decode() is free when a raster
     already exists. */
  function prime(priority) {
    if (!ring) return;
    for (let v = C - 2; v <= C + 2; v++) {
      const t = tiles[ring[((v % N) + N) % N]];
      const img = t && t.querySelector('img');
      if (!img) continue;
      img.loading = 'eager';
      img.fetchPriority = priority;
      if (img.decode) img.decode().catch(() => {});
    }
  }
  const rasterise = () => prime('high');

  /* A trackpad's horizontal swipe. Touch events do not fire on a desktop, so without
     this the row is arrow-only there — and a row that visibly moves but ignores the
     gesture everyone tries on it reads as broken rather than as button-driven.

     Wheel deltas are discrete, so this accumulates rather than following the finger:
     past a tile's worth of travel it steps once and keeps the remainder, which turns a
     continuous two-finger push into a run of steps at the speed it is pushed. The axis
     test is the same one the touch path uses, and preventDefault only fires once the
     gesture is committed to horizontal, so vertical scrolling over the row is untouched. */
  let wheelPx = 0, wheelIdle = 0, wheelLast = 0, wheelV = 0, wheelAxis = null;
  grid.addEventListener('wheel', e => {
    /* ── AXIS LOCKED FOR THE WHOLE GESTURE ───────────────────────────────────
       `if (!e.deltaX) return` only ignored a PERFECTLY vertical wheel, and almost no
       real one is: a two-finger scroll or a tilt-capable mouse leaks a pixel or two
       sideways per frame, those accumulated through drag(), and the rows crept left or
       right while someone was just reading down the page.
       The axis is decided on the opening frame of a gesture and held until the gesture
       ends, so incidental sideways delta inside a vertical scroll is discarded no matter
       how much of it arrives. This does NOT reintroduce the back/forward problem the
       note below describes: that gesture commits on frames where |deltaX| already
       dominates, which is exactly when this locks to horizontal and calls
       preventDefault on the very first one. */
    const now0 = performance.now();
    if (now0 - wheelLast > 220) wheelAxis = null;   /* a new gesture */
    if (!wheelAxis) {
      const ax = Math.abs(e.deltaX), ay = Math.abs(e.deltaY);
      if (!ax && !ay) return;
      wheelAxis = ax > ay ? 'h' : 'v';
    }
    if (wheelAxis === 'v') { wheelLast = now0; return; }   /* the page's scroll, not ours */
    /* PREVENTDEFAULT ON THE FIRST HORIZONTAL PIXEL, not once the gesture has been judged.
       A two-finger swipe on a trackpad is also the browser's back/forward gesture, and
       that gesture COMMITS on its opening frames — the old axis test here
       (|deltaX| <= |deltaY| * 1.2, return) let those frames through while the swipe was
       still diagonal, and by the time it read as horizontal the navigation had already
       started and could not be called off. There is nothing to disambiguate anyway: the
       row only ever wants horizontal delta, and the page's vertical scroll is untouched
       because a vertical wheel returns above. */
    e.preventDefault();
    ensureSeated();
    if (!seated) return;

    /* Follow the fingers rather than stepping. This used to accumulate to a third of a
       tile and then step() a whole one, which is a row that lurches in tile-sized jumps
       under a continuous gesture — the same complaint, in the other direction, that made
       the touch path follow the thumb. It goes through the same drag() the mouse uses,
       so a trackpad swipe and a click-drag are one mechanism and both rows move. */
    const now = performance.now();
    if (now - wheelLast > 220) wheelPx = 0;   /* a new gesture, not a continuation */
    if (!wheelPx) shared.spread(api, r => r.grab(), r => r.grab());
    wheelPx -= e.deltaX;                      /* deltaX is positive rightward like a finger going left */
    wheelV = (e.deltaX / Math.max(8, now - wheelLast));
    wheelLast = now;
    shared.spread(api, r => r.drag(wheelPx), r => r.drag(-wheelPx));

    /* A trackpad sends no "end" event, so idle IS the release — and releasing with the
       last velocity is what lets a flick keep coasting instead of stopping dead the
       instant the fingers lift. */
    clearTimeout(wheelIdle);
    wheelIdle = setTimeout(() => {
      wheelPx = 0;
      wheelAxis = null;
      shared.spread(api, r => r.release(wheelV), r => r.release(-wheelV));
    }, 90);
  }, { passive: false, signal: ac.signal });

  /* ── the ghost ─────────────────────────────────────────────────────────────
     A tile is an <a> around an <img>, and both are natively draggable: press and move
     and the browser lifts a translucent copy of the photograph, or the link's URL, and
     carries it with the cursor. That is a file-manager gesture landing on top of a
     carousel gesture — you go to push the row and you are dragging a picture out of the
     page. The elements are marked undraggable where they are built; this is the belt to
     that's braces, because a drag can also start from a text selection inside the tile. */
  grid.addEventListener('dragstart', e => e.preventDefault(), sig);

  /* Published for the review harnesses, the way the hero publishes __hero. The reel's
     state is all closure-private, so without this a harness can only infer what it is
     doing from the transform — which cannot tell "not drifting" from "drifting while
     something upstream is wrong". */
  const api = {
    get mode() { return mode; },
    get running() { return !!raf; },
    get inView() { return shared.inView; },
    get hovering() { return shared.hovering; },
    get seated() { return seated; },
    get pitch() { return pitch(); },
    get driftOK() { return driftOK(); },
    get dir() { return dir; },
    step, resume,
    /* ── driving the row from outside ───────────────────────────────────────
       The two rows are TIED: a drag on either one moves both, in opposite directions,
       because that is what makes them read as one mechanism rather than as two loops
       that happen to be stacked. So the gesture cannot live inside the row it started
       on. The handlers below MEASURE the finger; what MOVES a row is these four, called
       on every reel with the sign flipped for the ones the finger is not on. Everything
       they touch is closure-private, which is exactly why they have to exist. */
    grab() {
      ensureSeated();
      if (raf) { cancelAnimationFrame(raf); raf = 0; }   /* take the row off the spring */
      dragFrom = pos; vel = 0; dragging = false; spun = true;
    },
    /* dx is the finger's travel in PIXELS, positive rightward. pos counts the other way
       — it is how far the row has been pulled left — hence the minus. */
    drag(dx) {
      const q = pitch();
      if (!q || !seated) return;
      dragging = true;
      paintPos(dragFrom - dx / q);
    },
    /* v is px per ms of ROW travel, signed like pos: positive means the pictures are
       moving left. A release too slow to coast still commits to the nearest tile, so a
       small deliberate drag does not just sag back. */
    release(v) {
      const q = pitch();
      dragging = false;
      if (destroyed || !q || !seated) return;
      vel = v / q;
      if (Math.abs(vel) > VSTOP) { vel *= FLICK; run('fling'); }
      else springTo(Math.round(pos));
    },
    nudge(n) { ensureSeated(); if (seated) { spun = true; step(n); } },
    /* A press that turned out not to be a drag. It still has to clear `dragging` — that
       flag gates driftOK(), so a two-pixel jitter on the way to clicking a colourway
       would otherwise stop both rows permanently — but it must NOT snap, because a
       two-pixel correction is not a gesture and the row should carry on from where it
       is rather than jump to the nearest tile. */
    drop() { dragging = false; resume(); },
    /* Stop the row, hand its tiles back the way they came, and drop every listener,
       observer, timer and frame at once. The tiles are the shared pool's, not this
       reel's, so `order` and the row transform have to come off — a tile re-homed with
       order:7 still carrying would sort itself into the middle of its new row. The
       timers matter as much: a wheel-idle release or a ready() retry firing after this
       would drive the new reels with a dead one's numbers. */
    destroy() {
      destroyed = true;
      halt(); unring();
      clearTimeout(wheelIdle); clearTimeout(readyTimer); clearTimeout(readyRace);
      cancelAnimationFrame(seatRaf);
      if (idleHandle) (window.cancelIdleCallback ? cancelIdleCallback : clearTimeout)(idleHandle);
      ac.abort(); obs.forEach(o => o.disconnect());
    },
  };

  /* ── the gesture ───────────────────────────────────────────────────────────
     The row follows the finger, which is the whole point of the physics underneath: a
     threshold swipe decides AFTER you let go, so the row is inert while you are actually
     touching it. Here `pos` tracks the drag directly, so the pictures move with the
     thumb and the flick you give them is the flick they leave with.

     The axis lock is the same as the hero's and is still non-passive, because a browser
     may not begin scrolling until a cancellable listener has had its chance to cancel;
     a passive listener cannot refuse the page's vertical scroll and the row would fight
     it, and how far a release carries is how hard it was thrown rather than a number
     this file picked. */
  let tX = null, tY = null, moved = 0, upT = 0, lock = null, lastX = 0, lastMs = 0, vpx = 0;

  grid.addEventListener('touchstart', e => {
    ensureSeated();
    if (raf) { cancelAnimationFrame(raf); raf = 0; }   /* take the row off the spring */
    const t = e.touches[0];
    tX = t.clientX; tY = t.clientY; lastX = t.clientX; lastMs = performance.now();
    moved = 0; lock = null;
    shared.spread(api, r => r.grab(), r => r.grab());
  }, { passive: true, signal: ac.signal });

  grid.addEventListener('touchmove', e => {
    if (tX == null) return;
    const t = e.touches[0];
    const dx = t.clientX - tX, dy = t.clientY - tY;
    moved = Math.max(moved, Math.abs(dx));
    if (lock === null) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      lock = Math.abs(dx) > Math.abs(dy) * 1.2 ? 'x' : 'y';
    }
    if (lock !== 'x') return;
    if (e.cancelable) e.preventDefault();
    if (!pitch()) return;
    /* No rubber band and no limit: normalise() turns the ring underneath the drag, so
       the row genuinely has no end to reach. Resistance would be a lie about a ring. */
    const now = performance.now(), dtm = Math.max(8, now - lastMs);
    vpx = (lastX - t.clientX) / dtm;                /* px per ms, signed like pos */
    lastX = t.clientX; lastMs = now;
    shared.spread(api, r => r.drag(dx), r => r.drag(-dx));
  }, { passive: false, signal: ac.signal });

  grid.addEventListener('touchend', e => {
    if (tX == null) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - tX, dy = t.clientY - tY;
    const was = lock, wasDrag = dragging;
    tX = tY = null; lock = null; upT = performance.now();
    if (was === 'y') return;
    if (!wasDrag) {
      /* never crossed the lock: a flick fast enough to count still moves a tile */
      if (Math.abs(dx) >= 42 && Math.abs(dx) > Math.abs(dy) * 1.2) {
        const d = dx < 0 ? 1 : -1;
        shared.spread(api, r => r.nudge(d), r => r.nudge(-d));
      }
      return;
    }
    /* Hand the finger's velocity straight to the coast. Nothing projects a landing and
       nothing rounds anything yet: how far it goes is how hard it was thrown, which is
       the whole feel being asked for. */
    shared.spread(api, r => r.release(vpx), r => r.release(-vpx));
  }, { passive: true, signal: ac.signal });

  /* ── the mouse drag ────────────────────────────────────────────────────────
     Everything above this point is a TOUCH gesture, and touch events do not fire for a
     mouse — so on a desktop the row could be wheeled but not pushed, and a row that
     visibly moves under a trackpad swipe but ignores a click-and-drag reads as half
     wired. Pointer events cover both, but the touch path above is already tuned to the
     device it was written for (the axis lock, the cancellable preventDefault), so this
     handles the mouse only and leaves that alone.

     No axis lock: a mouse press on a row of photographs is not ambiguous the way a
     thumb on a scrolling page is, and there is no page scroll to refuse. Pointer capture
     keeps the drag alive when the cursor leaves the row — which it will, because the
     rows are only as tall as a tile and the other row is right there.

     Capture is taken on the first real MOVE, never on the press. A captured pointer
     retargets the compatibility click to the capturing element, so capturing on
     pointerdown sent every click to the grid instead of the tile underneath it — and a
     wall of links none of which could be clicked. Deferring it costs nothing: the drag
     only needs the capture once the cursor is actually travelling. */
  let mX = 0, mDown = false, mCaptured = false;
  grid.addEventListener('pointerdown', e => {
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    mDown = true; mCaptured = false; mX = e.clientX; lastX = e.clientX; lastMs = performance.now();
    moved = 0; vpx = 0;
    shared.spread(api, r => r.grab(), r => r.grab());
  }, sig);
  grid.addEventListener('pointermove', e => {
    if (!mDown || e.pointerType !== 'mouse') return;
    e.preventDefault();
    const dx = e.clientX - mX;
    moved = Math.max(moved, Math.abs(dx));
    if (!mCaptured && moved > 3) {
      mCaptured = true;
      try { grid.setPointerCapture(e.pointerId); } catch {}
    }
    const now = performance.now(), dtm = Math.max(8, now - lastMs);
    vpx = (lastX - e.clientX) / dtm;
    lastX = e.clientX; lastMs = now;
    shared.spread(api, r => r.drag(dx), r => r.drag(-dx));
  }, sig);
  const mUp = e => {
    if (!mDown || (e.pointerType && e.pointerType !== 'mouse')) return;
    mDown = false; upT = performance.now();
    if (mCaptured) try { grid.releasePointerCapture(e.pointerId); } catch {}
    /* A press that never really moved is a click on a colourway, not a drag: leave the
       row exactly where it is so the link underneath still behaves like a link. */
    if (moved > 3) shared.spread(api, r => r.release(vpx), r => r.release(-vpx));
    else shared.spread(api, r => r.drop(), r => r.drop());
  };
  grid.addEventListener('pointerup', mUp, sig);
  grid.addEventListener('pointercancel', mUp, sig);

  /* a swipe that ends on a tile is not a tap, and iOS fires the click anyway.
     3 is the same threshold that starts the drag: past it the press released as a
     gesture, so the click that trails it is the gesture's echo, not a choice. */
  grid.addEventListener('click', e => {
    if (moved > 3 && performance.now() - upT < 400) { e.preventDefault(); e.stopPropagation(); }
  }, { capture: true, signal: ac.signal });

  addEventListener('resize', () => {
    if (grid.clientWidth === gW) return;          /* an iOS URL-bar collapse is not a resize */
    invalidate();
    if (seated) paintPos(C);
  }, sig);
  /* A width change is now a re-measure, never a teardown: the pitch changes with the
     tile size but the transport does not change shape. */

  /* SYNCHRONOUSLY, before the first paint. This was a rAF, which meant the row painted
     once in DOM order at translate 0 and then jumped — thirteen `order` values and a
     six-pitch translate landing in the frame after the one the visitor already saw.
     That jump is the flash on first load. The stylesheet is render-blocking and this
     module is deferred, so layout is available here and reading offsetLeft now costs
     one forced layout in exchange for never showing the unseated row.
     The rest stay as fallbacks for the case where width is not knowable yet. */
  ensureSeated();
  seatRaf = requestAnimationFrame(ensureSeated);
  addEventListener('load', ensureSeated, sig);
  if (window.ResizeObserver) {
    const ro = new ResizeObserver(() => { invalidate(); ensureSeated(); });
    ro.observe(grid); obs.push(ro);
  }

  /* ── warming ───────────────────────────────────────────────────────────── */
  let warmed = false;
  function warm() {
    if (warmed || destroyed) return;
    warmed = true;
    /* Not under LEAN. Sixteen photographs pulled eagerly and mirrored into detached
       Images is exactly the memory a 2GB phone does not have; there the seats alone
       are raised, and the rest of the row fills as prime() reaches each tile. */
    if (!LEAN) {
      const pulled = new Set();
      grid.querySelectorAll('img').forEach(img => {
        img.loading = 'eager';
        img.fetchPriority = 'low';
        if (pulled.has(img.src)) return;
        pulled.add(img.src);
        const mirror = new Image();
        mirror.decoding = 'async';
        if (img.srcset) mirror.srcset = img.srcset;
        if (img.sizes) mirror.sizes = img.sizes;
        mirror.src = img.src;
      });
    }
    /* after the blanket `low` above, not before it: the five on screen are the only ones
       a visitor can see and they get the priority to match */
    prime('high');
  }
  /* four screens out, not two: the entrance crosses the whole set, so the whole set has
     to be there before it starts — and warming is low-priority, so starting earlier
     costs the hero nothing */
  obs.push(observe((es, o) => {
    if (!es.some(e => e.isIntersecting)) return;
    warm(); o.disconnect();
  }, { rootMargin: '400% 0px' }, section));
  /* AND ON IDLE, WHICHEVER COMES FIRST. Four screens of warning is plenty for a normal
     scroll and not nearly enough for a flick that crosses the page in one gesture —
     which is when the blank tiles were reported. Sixteen unique tile photographs at the
     CDN's cuts is well under a megabyte; fetched at idle priority after the page has
     settled it costs nothing anyone can feel, and by the time any scroll reaches the
     section they are decoded. warm() is idempotent, so whichever trigger arrives second
     does nothing. Not under LEAN, where nothing is fetched ahead of a scroll. */
  const idleWarm = () => {
    idleHandle = window.requestIdleCallback
      ? requestIdleCallback(() => warm(), { timeout: 3500 })
      : setTimeout(warm, 1800);
  };
  if (LEAN) { /* the 400% observer and the spin still warm the seats */ }
  else if (document.readyState === 'complete') idleWarm();
  else addEventListener('load', idleWarm, { once: true, signal: ac.signal });

  /* ── the entrance flick ────────────────────────────────────────────────── */
  let spun = false;
  /* Wait for EVERY tile on a desktop, not the three centre seats. The entrance used to
     move three colourways, so three seats was the whole of what it could show; it now
     cruises for 2.5 seconds and crosses the set about twice, and a spin through pictures
     that have not arrived is a spin through empty boxes. The timeout is longer to match
     — 4s, and it still goes ahead when it expires, because a slow connection should get
     a reel that spins late rather than a reel that never spins.

     A PHONE WAITS ON THE SEAT WINDOW ONLY — the tile on screen and two either side.
     Decoding all sixteen for a row that shows one at 74vw is ~41MP of raster held at
     once on the device least able to hold it, and it was; the other eleven decode as
     the spin reaches them, one 900px JPEG at a time. */
  function ready(then, waited = 0) {
    if (destroyed) return;
    const seats = innerWidth <= 700 && ring
      ? [-2, -1, 0, 1, 2].map(o => tiles[ring[((C + o) % N + N) % N]])
      : tiles;
    const imgs = seats.map(t => t && t.querySelector('img')).filter(Boolean);
    if (imgs.every(i => i.complete && i.naturalWidth) || waited > 4000) {
      /* `complete` only says the bytes arrived. A tile still has to be DECODED before it
         can paint, and a decode that happens as the tile crosses the screen is the
         "sign of loading" — one frame of nothing where a photograph should be. decode()
         resolves when there is a frame ready to paint, so waiting on the set means the
         spin starts with every picture already rasterised. Cheap when they are (which,
         after warming four screens out, they normally are) and bounded by the race
         below when they are not. */
      const all = Promise.all(imgs.map(i => (i.decode ? i.decode().catch(() => {}) : null)));
      Promise.race([all, new Promise(r => { readyRace = setTimeout(r, 900); })])
        .then(() => { if (!destroyed) then(); });
      return;
    }
    readyTimer = setTimeout(() => ready(then, waited + 120), 120);
  }
  /* The row is wider than the screen, and a row that is wider than the screen looks
     like a row that got cut off unless it moves. So the first time it comes into view it
     throws itself four colourways along and springs back into the seat — hard enough
     that the motion blur the transport already computes actually shows, which is what
     makes it read as thrown rather than as scrolled. The spring does the rest: it
     overshoots the landing by 13% and settles, so it arrives like something with weight.

     v0 is set before springTo because springTo does not touch velocity — it only aims
     the spring. Anyone who has already touched the row has found the affordance and
     does not need to be shown it, which is what `spun` guards. */
  function spin() {
    if (destroyed || spun || !rowMode() || slow.matches) return;
    ensureSeated();
    if (!seated || raf) return;
    spun = true;
    vel = SPINV * dir; spinT = 0;
    run('spin');
  }
  obs.push(observe((es, o) => {
    if (!es.some(e => e.isIntersecting)) return;
    o.disconnect();
    warm();
    ready(spin);
  }, { threshold: 0.35 }, section));
  return api;
}

  /* ── mounting, and re-mounting ────────────────────────────────────────────
     A reel captures its tiles when it is built, and on this page the tiles MOVE: a
     phone puts all thirteen colourways in one row, a desktop deals them into two, and
     dragging a window narrow or turning a phone on its side crosses between the two.
     A reel left holding tiles that now belong to the other row would be writing `order`
     and a transform onto elements another reel is also driving.

     So the breakpoint re-deals the pool and rebuilds the reels on top of it. It costs
     nothing at rest — the listener fires only on a crossing, which for almost every
     visitor is never — and it is the only version of this that is correct when it does
     happen. The row above runs right to left; the row below runs left to right. */
  function mount() {
    reels.forEach(r => r.destroy());
    reels.length = 0;
    for (const g of section.querySelectorAll('.hp-rooms-grid')) {
      if (!g.firstElementChild) continue;                  /* the phone's empty upper row */
      /* Two rows run against each other: the upper right-to-left, the lower left-to-right.
         A PHONE HAS ONLY THE LOWER ONE, and on its own it was running left-to-right —
         which reads backwards, because nothing is running the other way to make it a
         pair. Alone, it goes the way the top row would: right to left. */
      const alone = section.querySelectorAll('.hp-rooms-grid > .hp-room').length
        === g.querySelectorAll('.hp-room').length;
      const r = makeReel(g, (alone || g.classList.contains('hp-rooms-up')) ? 1 : -1, section, slow, shared);
      if (r) reels.push(r);
    }
    /* __reel stays the LOWER row — the harnesses in .hp/ have been reading it since
       there was only one, and on a phone it still is. __reels is every row, in DOM
       order, which on a phone is a list of one. */
    window.__reel = reels[reels.length - 1] || null;
    window.__reels = reels;
  }
  mount();

  const wake = () => reels.forEach(r => r.resume());
  for (const q of ['(max-width:700px)', '(orientation: landscape) and (max-height: 520px)'])
    matchMedia(q).addEventListener('change', () => { layoutRooms(); mount(); wake(); });

  /* ── what counts as hovering ───────────────────────────────────────────────
     A PHOTOGRAPH, not the section. This was pointerenter on .hp-rooms, which meant the
     rows froze the moment the cursor crossed the heading between them — and the heading
     is dead centre, so on the way to any tile you stopped the thing you were reaching
     for. Pausing belongs to the picture you are looking at; the band above it is just
     type. Delegated with pointerover/out because the tiles are replaced wholesale when
     the breakpoint changes, and enter/leave do not delegate. */
  const tileAt = e => (e.target.closest && e.target.closest('.hp-room')) || null;

  /* ── the name that follows the cursor ──────────────────────────────────────
     Thirteen photographs of the same object, and nothing on any of them says which
     colour it is. The tile is a link to that colourway, so the name is the one piece of
     information the picture cannot carry itself.

     It is set in the colourway's OWN colour, which is the small idea here — the label
     is a swatch as much as it is a word. Some of them are near-black (Eclipse, Matte
     Black) and would vanish on the chip's dark glass, so the colour is lifted toward
     white until it clears a contrast floor rather than being swapped for something
     legible: Eclipse still reads as Eclipse, just bright enough to read.

     One element, moved by transform. Building a tooltip per tile, or animating left/top,
     would put layout work on the cursor's path while two rows are already moving. */
  const tip = document.createElement('div');
  tip.className = 'hp-room-tip';
  tip.setAttribute('aria-hidden', 'true');            /* the link's own label says this already */
  /* On the body, not in the section: the chip is position:fixed and the section is
     overflow:clip, and putting a fixed child inside a clipping ancestor is a bet about
     containing blocks that there is no reason to take. */
  document.body.appendChild(tip);

  /* Relative luminance, then mix toward white until the text clears WCAG AA against the
     chip. The loop is the honest way to do it: the sRGB transfer curve makes solving for
     the mix analytically fussier than just walking it in twentieths. */
  const srgb = c => (c /= 255) <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  const lum = (r, g, b) => 0.2126 * srgb(r) + 0.7152 * srgb(g) + 0.0722 * srgb(b);
  const GROUND = lum(26, 21, 18);                     /* the chip's glass, near enough */
  const legible = hex => {
    const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
    if (!m) return '#f3ede4';
    const n = parseInt(m[1], 16);
    let r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
    for (let k = 0; k <= 20; k++) {
      const t = k / 20;
      const R = r + (255 - r) * t, G = g + (255 - g) * t, B = b + (255 - b) * t;
      if ((lum(R, G, B) + 0.05) / (GROUND + 0.05) >= 4.5 || k === 20)
        return `rgb(${Math.round(R)},${Math.round(G)},${Math.round(B)})`;
    }
  };

  let tipOn = false;
  const hideTip = () => { if (tipOn) { tipOn = false; tip.classList.remove('is-on'); tip.dataset.id = ''; } };
  /* one place that writes the name and its colour, so the pointer path and the
     follow-the-row path can never disagree about what the label says */
  const label = (t) => {
    const th = byId.get(t.dataset.id);
    if (!th) return false;
    tip.textContent = th.name;
    tip.style.color = legible(th.swatch);
    tip.dataset.id = t.dataset.id;
    return true;
  };
  const moveTip = (x, y) => {
    /* Flip to the left of the cursor near the right edge, and ride above it near the
       bottom — a label that leaves the window is worse than no label. */
    const w = tip.offsetWidth, h = tip.offsetHeight;
    const right = x + 18 + w > innerWidth - 8;
    const low = y + 20 + h > innerHeight - 8;
    tip.style.transform = `translate3d(${Math.round(right ? x - 18 - w : x + 18)}px,${Math.round(low ? y - 14 - h : y + 20)}px,0)`;
  };

  /* ── PAUSING WAITS FOR THE CURSOR TO ACTUALLY REST ────────────────────────
     Hovering used to pause on the first pointerover, so the rows froze whenever the
     cursor merely crossed them — on its way to the nav, to a link below, to anywhere.
     Pausing is meant to say "I am looking at this one", and crossing a strip in 150ms
     says nothing of the kind. So a tile now ARMS a dwell timer instead of pausing, and
     only stillness for DWELL_MS actually stops the rows. Any real movement re-arms it,
     which also means the rows start again the moment you move on rather than staying
     frozen until you leave the section.
     The LABEL still appears immediately — that is an affordance, not an interruption,
     and the complaint was only ever about the motion stopping. */
  const DWELL_MS = 260;
  const DWELL_SLOP = 6;              /* px of hand tremor that still counts as resting */
  let dwellT = 0, dwellX = 0, dwellY = 0;
  const armDwell = (x, y) => {
    dwellX = x; dwellY = y;
    clearTimeout(dwellT);
    dwellT = setTimeout(() => { dwellT = 0; shared.hovering = true; }, DWELL_MS);
  };
  const cancelDwell = () => {
    clearTimeout(dwellT); dwellT = 0;
    if (shared.hovering) { shared.hovering = false; wake(); }
  };

  section.addEventListener('pointerover', e => {
    if (e.pointerType === 'touch') return;
    const t = tileAt(e);
    if (!t) return;
    armDwell(e.clientX, e.clientY);
    px = e.clientX; py = e.clientY;
    if (!label(t)) return;
    moveTip(px, py);
    if (!tipOn) { tipOn = true; tip.classList.add('is-on'); }
    startFollow();
  });
  /* Where the cursor last was, so the label can be re-checked when nothing has moved it.
     A pointer event only fires when the POINTER moves; these rows move underneath a
     still cursor whenever the wheel drives them, and the browser does not re-dispatch
     pointerover for the tile that slid into place. So the name went stale — you scrolled
     the strip with the cursor resting on it and kept reading the colour that used to be
     there. */
  let px = 0, py = 0;
  section.addEventListener('pointermove', e => {
    if (e.pointerType === 'touch') return;
    px = e.clientX; py = e.clientY;
    if (tipOn) moveTip(px, py);
    /* Moved for real: the cursor is travelling, not resting. Let the rows run and start
       the clock over. Below the slop it is tremor and the dwell keeps counting. */
    if (tileAt(e) && (Math.abs(px - dwellX) > DWELL_SLOP || Math.abs(py - dwellY) > DWELL_SLOP)) {
      cancelDwell();
      armDwell(px, py);
    }
  });

  /* Re-hit-test every frame the label is up. elementFromPoint is a cheap read and this
     only runs while a label is actually showing, which is only while a cursor is resting
     on a photograph. */
  let tipRaf = 0;
  const follow = () => {
    tipRaf = 0;
    if (!tipOn) return;
    const el = document.elementFromPoint(px, py);
    const t = el && el.closest && el.closest('.hp-room');
    if (t) {
      if (t.dataset.id !== tip.dataset.id) label(t);
    } else if (!el || !el.closest || !el.closest('.hp-rooms')) {
      /* genuinely left the section */
      hideTip();
      return;
    }
    /* ...and if it is inside the section but not on a tile, HOLD. There is a 3px seam
       between every pair of tiles, and a row sliding under a still cursor puts one of
       those under the point about once a tile. Treating that as "the cursor left" killed
       the label on the first seam it crossed and never brought it back — which is the
       reported behaviour: hover, scroll, and the name stops keeping up. */
    tipRaf = requestAnimationFrame(follow);
  };
  const startFollow = () => { if (!tipRaf) tipRaf = requestAnimationFrame(follow); };
  section.addEventListener('pointerout', e => {
    if (e.pointerType === 'touch') return;
    /* out fires when moving BETWEEN two tiles as well as when leaving for good; only
       the second should restart the rows or drop the label */
    if (e.relatedTarget && e.relatedTarget.closest && e.relatedTarget.closest('.hp-room')) return;
    cancelDwell();
    hideTip();
    wake();
  });
  /* A row can slide a different colourway under a still cursor, and the label has to
     follow the picture rather than the pointer. */
  addEventListener('scroll', hideTip, { passive: true });
  addEventListener('visibilitychange', wake);
  new IntersectionObserver(es => {
    shared.inView = es.some(e => e.isIntersecting);
    wake();
  }, { rootMargin: '80px 0px', threshold: 0 }).observe(section);
})();

/* The hamburger sheet is tao-mockup-header.js's — see the note above the hero. */
